import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import useSWR, { mutate as mutateSwr } from 'swr';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  LinearProgress,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  type SxProps,
} from '@mui/material';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import { colors, StatusBadge } from '@kangaroo/ui';
import type { SavedTrack, TrackContribution } from '@kangaroo/shared';

import { GpsSpeedTrackMap } from '../../components/telemetry/GpsSpeedTrackMap';
import { MapPlaceSearch, type MapPlaceSelection } from '../../components/telemetry/MapPlaceSearch';
import { TelemetryReplayBar } from '../../components/telemetry/TelemetryReplayBar';
import { dataFilesListKey, fetchDataFileContent, fetchDataFiles } from '../../features/dataFiles';
import {
  createTelemetryTrack,
  updateTelemetrySessionContext,
  fetchTelemetryTracks,
} from '../../features/telemetryContext';
import {
  buildTelemetrySessionOptions,
  pickInitialTelemetrySession,
  type TelemetrySessionOption,
} from '../../lib/telemetrySessions';
import { useAuthStore } from '../../store/authStore';
import {
  buildMapPointsForGpsWindow,
  formatTraceClock,
  isMyChronSessionExport,
  projectTrackPointsWithReferenceBounds,
  sessionGpsCenter,
  type MyChronSessionExport,
  type ReplayScope,
  type TrackMapPoint,
} from '../../lib/telemetrySession';
import {
  buildContributionDraftPayload,
  appendManualLapDrawPoint,
  buildManualTrackGeometry,
  buildMapPointGroupsForReslicedLaps,
  buildReslicedLapReplayTimeline,
  buildReslicedSessionReplayTimeline,
  buildStartLineFromEndpoints,
  closeManualLapDrawPolyline,
  detectDirection,
  fractionToPolylinePoint,
  healCircuitPolylineForDisplay,
  insertCornerAtCoord,
  isManualLapDrawComplete,
  isManualMapSessionKey,
  manualLapDrawLengthM,
  geometryFromCircuitDraft,
  listCatalogTrackOptions,
  loadSeedSession,
  matchSessionToCircuitDraft,
  matchGpsTraceToCircuitDraft,
  moveCornerToCoord,
  recalibrateTrackGeometry,
  removeCornerById,
  removeManualLapDrawPointAtIndex,
  reopenManualLapDrawPolyline,
  resolveCatalogTrackId,
  resolveDraftCornerRestore,
  resolveTrackForSession,
  resliceLapsAtStartLine,
  reslicedFlyingLaps,
  reslicedLapKindCounts,
  runEnrichmentPipeline,
  runManualTrackEnrichmentPipeline,
  runGoProEnrichmentPipeline,
  snapCoordToPolyline,
  startFractionForStartLine,
  startLineLengthM,
  undoManualLapDrawPoint,
  type ContributionStatus,
  type CornerApexSuggestion,
  type EnrichmentPipelineResult,
  type GpsCoord,
  type RecalibratedTrackGeometry,
  type ReslicedLapWindow,
  type StartLineSuggestion,
  type TrackDataLayer,
} from '../../lib/trackEnrichment';
import {
  createTrackContributionDraftId,
  fetchTrackContributionDraftForSession,
  fetchTrackContributionDraftsForTracks,
  fetchTeamTrackContributions,
  formatTrackContributionDraftLabel,
  saveTrackContribution,
  saveTrackContributionDraft,
  teamTrackContributionsListKey,
  trackContributionDraftsListKey,
} from '../../lib/trackContributions';
import { useTelemetryReplay } from '../../lib/useTelemetryReplay';
import { loadSessionTrackBinding } from '../../lib/resolveSessionTrackBinding';
import { isGoProEnrichmentSessionKey, loadStashedGoProEnrichmentSession } from '../../lib/goproEnrichmentEditor';
import {
  defaultEnabledOsmLayoutIds,
  deriveOsmTrackLayouts,
  osmOverlayGuideColorsForLayoutIndex,
  osmOverlayViewportCenter,
  parseOsmTrackOverlayXml,
  type OsmTrackOverlayImport,
} from '../../lib/osmTrackOverlay';

type EditMode = 'none' | 'draw-lap' | 'place-sf' | 'move-turn' | 'add-turn';

const OVERLAY_BG = 'rgba(7, 17, 28, 0.9)';
const PANEL_GAP = 16;
const PANEL_PAD = 2.25;
const SECTION_GAP = 1.75;
const LEFT_PANEL_WIDTH = 296;
const RIGHT_PANEL_WIDTH = 304;
const LAP_STRIP_BASE_HEIGHT = 88;
const REPLAY_BAR_HEIGHT = 76;

const layerToggleSx = {
  width: '100%',
  '& .MuiToggleButtonGroup-grouped': {
    flex: 1,
    borderColor: 'rgba(255,255,255,0.12)',
  },
  '& .MuiToggleButton-root': {
    flex: 1,
    px: 0.85,
    py: 0.75,
    minHeight: 34,
    fontSize: '0.68rem',
    fontWeight: 700,
    color: 'text.secondary',
    textTransform: 'none',
    gap: 0.5,
    lineHeight: 1.2,
    '&.Mui-selected': {
      bgcolor: `${colors.primary[500]}33`,
      color: colors.primary[300],
      borderColor: `${colors.primary[500]}66`,
      '&:hover': { bgcolor: `${colors.primary[500]}44` },
    },
  },
} as const;


function MapOverlay({
  sx,
  children,
  maxWidth,
  interactive = true,
}: {
  sx?: SxProps;
  children: ReactNode;
  maxWidth?: number | string;
  interactive?: boolean;
}) {
  return (
    <Box
      sx={{
        bgcolor: OVERLAY_BG,
        backdropFilter: 'blur(10px)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: 2,
        boxShadow: '0 12px 40px rgba(0,0,0,0.45)',
        maxWidth,
        pointerEvents: interactive ? 'auto' : 'none',
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

function coordsToMapPoints(coords: GpsCoord[], speedKph = 75): TrackMapPoint[] {
  if (coords.length === 0) {
    return [];
  }

  const gps = coords.map((coord, index) => ({
    lat: coord.lat,
    lon: coord.lon,
    alt: 0,
    speed: speedKph / 3.6,
    timecodeMs: index * 120,
    fix: 3,
    satellites: 10,
  }));

  return projectTrackPointsWithReferenceBounds(gps, gps, 1200);
}

function pickPrimaryTrackGroup(groups: TrackMapPoint[][]) {
  if (groups.length === 0) {
    return [];
  }

  return groups.reduce((best, group) => (group.length > best.length ? group : best), groups[0]!);
}

function lookupTrackLabel(trackId: string, teamTracks: SavedTrack[]) {
  return (
    teamTracks.find((track) => track.id === trackId)?.name ??
    listCatalogTrackOptions().find((track) => track.id === trackId)?.name ??
    trackId
  );
}

function pickMapLayoutTrackId(input: {
  matchedTrackId?: string | null;
  preferredTrackId?: string | null;
  catalogTrackId?: string | null;
  teamTracks: SavedTrack[];
}) {
  const matched =
    input.matchedTrackId && input.matchedTrackId !== 'unknown' ? input.matchedTrackId : null;

  return (
    matched ??
    input.preferredTrackId ??
    input.catalogTrackId ??
    input.teamTracks[0]?.id ??
    listCatalogTrackOptions()[0]?.id ??
    null
  );
}

const DEFAULT_MAP_CENTER: GpsCoord = { lat: 36.16451, lon: -115.8969 };

const REPLAY_MAP_MAX_POINTS = 2500;

function formatPercent(value: number) {
  return `${Math.round(value * 100)}%`;
}

function formatReslicedLapLabel(lap: ReslicedLapWindow) {
  const time = formatTraceClock(lap.durationSec);
  if (lap.kind === 'out') {
    return lap.complete ? `L${lap.lapNumber} · Out · ${time}` : `Out · ${time}`;
  }
  if (lap.kind === 'in') {
    return `In · ${time}`;
  }
  if (lap.kind === 'crash') {
    return `Crash · ${time}`;
  }
  return `L${lap.lapNumber} · ${time}`;
}

function formatResliceSummary(counts: ReturnType<typeof reslicedLapKindCounts>) {
  const parts: string[] = [];
  if (counts.flying > 0) {
    parts.push(`${counts.flying} flying`);
  }
  if (counts.out > 0) {
    parts.push(`${counts.out} out`);
  }
  if (counts.in > 0) {
    parts.push(`${counts.in} in`);
  }
  if (counts.crash > 0) {
    parts.push(`${counts.crash} crash`);
  }
  return parts.length > 0 ? parts.join(' · ') : '0 laps';
}

function reslicedLapChipStyles(lap: ReslicedLapWindow, selected: boolean) {
  const palette = {
    flying: {
      base: 'rgba(168, 85, 247, 0.12)',
      selected: 'rgba(168, 85, 247, 0.28)',
      border: 'rgba(168, 85, 247, 0.25)',
      borderSelected: 'rgba(168, 85, 247, 0.55)',
      color: '#C4B5FD',
    },
    out: {
      base: 'rgba(245, 158, 11, 0.16)',
      selected: 'rgba(245, 158, 11, 0.32)',
      border: 'rgba(245, 158, 11, 0.35)',
      borderSelected: 'rgba(245, 158, 11, 0.55)',
      color: '#FBBF24',
    },
    in: {
      base: 'rgba(148, 163, 184, 0.14)',
      selected: 'rgba(148, 163, 184, 0.28)',
      border: 'rgba(148, 163, 184, 0.32)',
      borderSelected: 'rgba(148, 163, 184, 0.5)',
      color: '#CBD5E1',
    },
    crash: {
      base: 'rgba(239, 68, 68, 0.14)',
      selected: 'rgba(239, 68, 68, 0.28)',
      border: 'rgba(239, 68, 68, 0.35)',
      borderSelected: 'rgba(239, 68, 68, 0.55)',
      color: '#FCA5A5',
    },
  }[lap.kind];

  return {
    bgcolor: selected ? palette.selected : palette.base,
    color: palette.color,
    border: `1px solid ${selected ? palette.borderSelected : palette.border}`,
  };
}

async function loadTelemetrySessionExport(option: TelemetrySessionOption): Promise<MyChronSessionExport> {
  const payload = option.dataFile
    ? await fetchDataFileContent(option.dataFile.id, 'full')
    : await loadSeedSession(option.loadUrl);

  if (!isMyChronSessionExport(payload)) {
    throw new Error('Session JSON is missing gps/laps/series data');
  }

  return payload;
}

function SessionPicker({
  options,
  value,
  loading,
  disabled,
  onSelect,
}: {
  options: TelemetrySessionOption[];
  value: TelemetrySessionOption | null;
  loading?: boolean;
  disabled?: boolean;
  onSelect: (option: TelemetrySessionOption) => void;
}) {
  return (
    <Autocomplete
      size="small"
      options={options}
      value={value}
      loading={loading}
      disabled={disabled || options.length === 0}
      onChange={(_, option) => {
        if (option) {
          onSelect(option);
        }
      }}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(left, right) => left.key === right.key}
      sx={{ minWidth: 220, maxWidth: 340, pointerEvents: 'auto' }}
      slotProps={{
        paper: {
          sx: { bgcolor: 'rgba(7, 17, 28, 0.98)', border: '1px solid rgba(255,255,255,0.08)' },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={options.length === 0 ? 'No sessions' : 'Select session'}
          inputProps={{
            ...params.inputProps,
            style: { fontSize: '0.78rem' },
          }}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.key}>
          <Stack sx={{ py: 0.25 }}>
            <Typography variant="body2" sx={{ fontSize: '0.78rem', fontWeight: 700 }}>
              {option.title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.66rem' }}>
              {option.subtitle}
            </Typography>
          </Stack>
        </Box>
      )}
    />
  );
}

function layerLabel(layer: TrackDataLayer) {
  switch (layer) {
    case 'draft':
      return 'Draft circuit';
    case 'global':
      return 'Global baseline';
  }
}

export function TrackEnrichmentPage() {
  const token = useAuthStore((state) => state.token);
  const [searchParams] = useSearchParams();
  const requestedSessionKey = searchParams.get('session');
  const requestedDraftId = searchParams.get('draft');
  const requestedSource = searchParams.get('source');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessionKey, setSessionKey] = useState<string | null>(null);
  const [activeSessionOption, setActiveSessionOption] = useState<TelemetrySessionOption | null>(null);
  const [session, setSession] = useState<MyChronSessionExport | null>(null);
  const [result, setResult] = useState<EnrichmentPipelineResult | null>(null);
  const [contributionStatus, setContributionStatus] = useState<ContributionStatus>('pending');
  const [dataLayer, setDataLayer] = useState<TrackDataLayer>('draft');
  const [showSuggestionOverlay, setShowSuggestionOverlay] = useState(true);
  const [editMode, setEditMode] = useState<EditMode>('none');
  const [selectedCornerId, setSelectedCornerId] = useState<string | null>(null);
  const [turnHoverCoord, setTurnHoverCoord] = useState<GpsCoord | null>(null);
  const [selectedLapVertexIndex, setSelectedLapVertexIndex] = useState<number | null>(null);
  const [manualCorners, setManualCorners] = useState<CornerApexSuggestion[] | null>(null);
  const [startLineFraction, setStartLineFraction] = useState<number | null>(null);
  const [customStartLine, setCustomStartLine] = useState<StartLineSuggestion | null>(null);
  const [startLineDrawAnchor, setStartLineDrawAnchor] = useState<GpsCoord | null>(null);
  const [calibratedGeometry, setCalibratedGeometry] = useState<RecalibratedTrackGeometry | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(true);
  const [reviewOpen, setReviewOpen] = useState(true);
  const [activeDraftId, setActiveDraftId] = useState<string | null>(null);
  const [draftNotice, setDraftNotice] = useState<string | null>(null);
  const [draftSaving, setDraftSaving] = useState(false);
  const [creatingTrackFromLap, setCreatingTrackFromLap] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [draftCircuitName, setDraftCircuitName] = useState('');
  const [workingTrackIdOverride, setWorkingTrackIdOverride] = useState<string | null>(null);
  const [sourcePolylineOverride, setSourcePolylineOverride] = useState<GpsCoord[] | null>(null);
  const [selectedReslicedLapNumber, setSelectedReslicedLapNumber] = useState(1);
  const [replayScope, setReplayScope] = useState<ReplayScope>('lap');
  const [lapResliceGeneration, setLapResliceGeneration] = useState(0);
  const [teamTracks, setTeamTracks] = useState<SavedTrack[]>([]);
  const [sessionPreferredTrackId, setSessionPreferredTrackId] = useState<string | null>(null);
  const [manualTrackId, setManualTrackId] = useState<string | null>(null);
  const [mapLayoutMode, setMapLayoutMode] = useState(false);
  const [mapViewport, setMapViewport] = useState<{ lat: number; lon: number; zoom?: number; label?: string } | null>(
    null,
  );
  const [mapLayoutTrackId, setMapLayoutTrackId] = useState('');
  const [manualLapPolyline, setManualLapPolyline] = useState<GpsCoord[]>([]);
  const [lapDrawClosed, setLapDrawClosed] = useState(false);
  const [showSectorMarkers, setShowSectorMarkers] = useState(false);
  const [showLayoutOutline, setShowLayoutOutline] = useState(true);
  const [osmOverlayImport, setOsmOverlayImport] = useState<OsmTrackOverlayImport | null>(null);
  const [osmEnabledLayoutIds, setOsmEnabledLayoutIds] = useState<string[]>([]);
  const [showOsmOverlay, setShowOsmOverlay] = useState(true);
  const osmFileInputRef = useRef<HTMLInputElement>(null);
  const skipGeometryRecalcRef = useRef(false);
  const preferFlyingLapRef = useRef(true);
  const initialSessionLoadedRef = useRef(false);
  const mapLayoutBootstrapRef = useRef(false);

  const { data: dataFilesPage, isLoading: dataFilesLoading } = useSWR(
    dataFilesListKey({ perPage: 100 }),
    () => fetchDataFiles({ perPage: 100 }),
  );

  const sessionOptions = useMemo(
    () => buildTelemetrySessionOptions(dataFilesPage?.data ?? []),
    [dataFilesPage?.data],
  );

  const selectedSessionOption = useMemo(
    () => sessionOptions.find((option) => option.key === sessionKey) ?? activeSessionOption,
    [activeSessionOption, sessionKey, sessionOptions],
  );

  const trackPickerOptions = useMemo(() => {
    const options: Array<{ id: string; name: string; source: 'team' | 'catalog' }> = teamTracks.map((track) => ({
      id: track.id,
      name: track.name,
      source: 'team',
    }));

    for (const catalogTrack of listCatalogTrackOptions()) {
      if (!options.some((option) => option.id === catalogTrack.id)) {
        options.push({ ...catalogTrack, source: 'catalog' });
      }
    }

    return options;
  }, [teamTracks]);

  const isMapLayoutMode = mapLayoutMode || isManualMapSessionKey(sessionKey);

  const applyMapLayout = useCallback(
    (input: {
      trackId: string;
      trackName: string;
      center: GpsCoord;
      venue: string;
      notice: string;
      sessionKey?: string;
      keepSession?: boolean;
      loadedSession?: MyChronSessionExport | null;
      sessionOption?: TelemetrySessionOption | null;
    }) => {
      setError(null);
      setEditMode('draw-lap');
      setSelectedCornerId(null);
      setSelectedLapVertexIndex(null);
      setManualCorners(null);
      setManualLapPolyline([]);
      setLapDrawClosed(false);
      setStartLineFraction(null);
      setCustomStartLine(null);
      setStartLineDrawAnchor(null);
      setActiveDraftId(null);
      setWorkingTrackIdOverride(null);
      setSourcePolylineOverride(null);
      setDraftNotice(input.notice);
      setDraftCircuitName(input.venue.trim() || input.trackName);
      setContributionStatus('pending');
      setDataLayer('draft');
      setSelectedReslicedLapNumber(1);
      setReplayScope('lap');
      setLapResliceGeneration(0);
      setManualTrackId(input.trackId);
      setMapLayoutTrackId(input.trackId);
      setMapLayoutMode(true);
      setShowSectorMarkers(false);
      setShowLayoutOutline(true);
      setMapViewport({
        lat: input.center.lat,
        lon: input.center.lon,
        zoom: 17,
        label: input.venue.trim() || input.trackName,
      });

      if (!input.keepSession) {
        setSession(null);
        setActiveSessionOption(null);
      } else if (input.loadedSession) {
        setSession(input.loadedSession);
      }
      if (input.sessionOption) {
        setActiveSessionOption(input.sessionOption);
      }

      const pipeline = runManualTrackEnrichmentPipeline({
        trackId: input.trackId,
        trackName: input.trackName,
        center: input.center,
        venue: input.venue.trim() || input.trackName,
        sessionKey: input.sessionKey,
      });

      setSessionKey(pipeline.sessionKey);
      setResult(pipeline);
      setCalibratedGeometry(
        buildManualTrackGeometry({
          mapCenter: input.center,
          startLine: null,
          corners: [],
        }),
      );
      setLoading(false);
    },
    [],
  );

  const startMapLayout = useCallback(
    (trackId: string, center: GpsCoord, venue: string) => {
      const trackName = lookupTrackLabel(trackId, teamTracks);
      if (!trackId) {
        setError('Select a track before starting map layout.');
        return;
      }

      setLoading(true);
      applyMapLayout({
        trackId,
        trackName,
        center,
        venue: venue.trim() || trackName,
        notice: 'Click the map to trace the circuit outline, then close the lap.',
      });
    },
    [applyMapLayout, teamTracks],
  );

  const handlePlaceSelect = useCallback((place: MapPlaceSelection) => {
    setMapViewport({ lat: place.lat, lon: place.lon, zoom: 17, label: place.label });
    setDraftCircuitName((current) => current.trim() || place.label);
  }, []);

  const handleOsmOverlayFile = useCallback(async (file: File) => {
    try {
      const parsed = parseOsmTrackOverlayXml(await file.text());
      const derivedLayouts = deriveOsmTrackLayouts(parsed);
      const enabledIds = defaultEnabledOsmLayoutIds(derivedLayouts);
      setOsmOverlayImport(parsed);
      setOsmEnabledLayoutIds(enabledIds);
      setShowOsmOverlay(true);

      if (parsed.bounds) {
        const center = osmOverlayViewportCenter(parsed.bounds);
        setMapViewport({
          lat: center.lat,
          lon: center.lon,
          zoom: 17,
          label: file.name.replace(/\.osm$/i, ''),
        });
      }

      const racewayCount = parsed.ways.filter((way) => way.kind === 'raceway').length;
      const buildingCount = parsed.ways.filter((way) => way.kind === 'building').length;
      const otherCount = parsed.ways.length - racewayCount - buildingCount;
      const parts = [
        racewayCount > 0 ? `${racewayCount} raceway${racewayCount === 1 ? '' : 's'}` : null,
        buildingCount > 0 ? `${buildingCount} building${buildingCount === 1 ? '' : 's'}` : null,
        otherCount > 0 ? `${otherCount} other way${otherCount === 1 ? '' : 's'}` : null,
      ].filter(Boolean);
      setDraftNotice(
        `OSM overlay loaded (${parts.join(', ')}) — toggle layers in the left panel while tracing.`,
      );
    } catch (importError) {
      setDraftNotice(importError instanceof Error ? importError.message : 'Failed to import OSM file');
    }
  }, []);

  const handleClearOsmOverlay = useCallback(() => {
    setOsmOverlayImport(null);
    setOsmEnabledLayoutIds([]);
    setShowOsmOverlay(true);
    if (osmFileInputRef.current) {
      osmFileInputRef.current.value = '';
    }
  }, []);

  useEffect(() => {
    if (!token || teamTracks.length > 0) {
      return;
    }

    void fetchTelemetryTracks()
      .then((tracks) => {
        setTeamTracks(tracks);
        if (!mapLayoutTrackId && tracks[0]) {
          setMapLayoutTrackId(tracks[0].id);
        }
      })
      .catch(() => undefined);
  }, [mapLayoutTrackId, teamTracks.length, token]);

  const rerunPipelineForTrack = useCallback(
    (loadedSession: MyChronSessionExport, optionKey: string, nextManualTrackId: string | null) => {
      const pipeline = runEnrichmentPipeline(loadedSession, optionKey, {
        savedTracks: teamTracks,
        preferredTrackId: sessionPreferredTrackId,
        manualTrackId: nextManualTrackId,
      });
      if (pipeline) {
        setWorkingTrackIdOverride(null);
        setSourcePolylineOverride(null);
        setResult(pipeline);
      }
    },
    [sessionPreferredTrackId, teamTracks],
  );

  const loadSession = useCallback(
    async (
      option: TelemetrySessionOption,
      options?: {
        draftId?: string;
        skipDraftLoad?: boolean;
        sessionExport?: MyChronSessionExport;
      },
    ) => {
      try {
        setLoading(true);
        setError(null);
        setSessionKey(option.key);
        setActiveSessionOption(option);
        setEditMode('none');
        setSelectedCornerId(null);
        setManualCorners(null);
        setStartLineFraction(null);
        setCustomStartLine(null);
        setStartLineDrawAnchor(null);
        setCalibratedGeometry(null);
        setActiveDraftId(null);
        setWorkingTrackIdOverride(null);
        setSourcePolylineOverride(null);
        setDraftNotice(null);
        setDraftCircuitName('');
        setContributionStatus('pending');
        setDataLayer('draft');
        setSelectedReslicedLapNumber(1);
        setReplayScope('lap');
        setLapResliceGeneration(0);
        preferFlyingLapRef.current = true;
        setManualTrackId(null);
        setMapLayoutMode(false);
        setMapViewport(null);
        setManualLapPolyline([]);
        setLapDrawClosed(false);
        setShowSectorMarkers(true);
        setShowLayoutOutline(true);
        setTeamTracks([]);
        setSessionPreferredTrackId(null);

        const loadedSession = options?.sessionExport ?? await loadTelemetrySessionExport(option);

        const trackBinding = await loadSessionTrackBinding(loadedSession, option.key, Boolean(token));
        setTeamTracks(trackBinding.tracks);
        setSessionPreferredTrackId(trackBinding.preferredTrackId);

        const catalogTrackId = resolveCatalogTrackId(loadedSession);
        const draftTrackIds = [
          ...trackBinding.tracks.map((track) => track.id),
          ...(catalogTrackId ? [catalogTrackId] : []),
        ];
        const allTeamContributions =
          token ? await fetchTeamTrackContributions(undefined, 'full').catch(() => []) : [];
        const allCircuitDrafts = allTeamContributions.filter(
          (contribution) =>
            draftTrackIds.includes(contribution.trackId)
            && (contribution.payload.gpsPolyline?.length ?? 0) >= 16,
        );
        const circuitDraftMatch = loadedSession.format === 'gopro-gps'
          ? matchGpsTraceToCircuitDraft(loadedSession.gps, allCircuitDrafts)
          : matchSessionToCircuitDraft(loadedSession, allCircuitDrafts);

        const pipeline = loadedSession.format === 'gopro-gps'
          ? runGoProEnrichmentPipeline(loadedSession, option.key, {
              savedTracks: trackBinding.tracks,
              preferredTrackId: circuitDraftMatch?.trackId ?? trackBinding.preferredTrackId,
              circuitDraftMatch,
            })
          : runEnrichmentPipeline(loadedSession, option.key, {
              savedTracks: trackBinding.tracks,
              preferredTrackId: circuitDraftMatch?.trackId ?? trackBinding.preferredTrackId,
              circuitDraftMatch,
            });
        if (!pipeline) {
          const matchedTrack = resolveTrackForSession(loadedSession, {
            savedTracks: trackBinding.tracks,
            preferredTrackId: circuitDraftMatch?.trackId ?? trackBinding.preferredTrackId,
          });
          const trackId = pickMapLayoutTrackId({
            matchedTrackId: matchedTrack.id,
            preferredTrackId: trackBinding.preferredTrackId,
            catalogTrackId,
            teamTracks: trackBinding.tracks,
          });
          const center = sessionGpsCenter(loadedSession) ?? DEFAULT_MAP_CENTER;
          if (trackId) {
            const trackName = lookupTrackLabel(trackId, trackBinding.tracks);
            const venue = loadedSession.metadata?.venue?.trim() || trackName;
            applyMapLayout({
              trackId,
              trackName,
              center,
              venue,
              notice:
                'This session could not be analyzed automatically. Trace the circuit on the map, then mark start/finish and turns.',
              sessionKey: option.key,
              keepSession: true,
              loadedSession,
              sessionOption: option,
            });
            return;
          }

          setSession(loadedSession);
          setResult(null);
          setError('Pipeline could not analyze this session.');
          return;
        }

        const sourcePoly = pipeline.contribution.payload.gpsPolyline;
        const trackId = pipeline.contribution.payload.trackId;
        const legacyTrackIds =
          catalogTrackId && catalogTrackId !== trackId ? [catalogTrackId] : [];
        let draft = null;

        if (token && !options?.skipDraftLoad) {
          try {
            if (options?.draftId) {
              draft =
                allTeamContributions.find(
                  (contribution) =>
                    contribution.id === options.draftId
                    && (contribution.trackId === trackId || legacyTrackIds.includes(contribution.trackId)),
                )
                ?? (await fetchTrackContributionDraftForSession(trackId, option.key, {
                  draftId: options.draftId,
                  fallbackTrackIds: legacyTrackIds,
                }));
            } else {
              draft =
                allTeamContributions.find(
                  (contribution) =>
                    contribution.sessionKey === option.key
                    && (contribution.trackId === trackId || legacyTrackIds.includes(contribution.trackId)),
                ) ?? null;
              if (!draft && !circuitDraftMatch) {
                draft = await fetchTrackContributionDraftForSession(trackId, option.key, {
                  fallbackTrackIds: legacyTrackIds,
                });
              }
            }
          } catch (draftError) {
            setDraftNotice(
              draftError instanceof Error ? draftError.message : 'Failed to load draft',
            );
          }
        } else if (!token) {
          setDraftNotice('Sign in to load or save drafts.');
        }

        const restoredFraction = draft?.payload.startFraction;
        const restoredStartLine = draft?.payload.startLineSuggestion ?? null;
        let geometry: RecalibratedTrackGeometry;
        let cornerRestore = {
          manualCorners: null as CornerApexSuggestion[] | null,
          skippedPartialDraft: false,
          draftCornerCount: 0,
        };

        if (draft) {
          geometry = geometryFromCircuitDraft(draft.payload);
          cornerRestore = resolveDraftCornerRestore(
            draft.payload.cornerApexSuggestions as CornerApexSuggestion[] | undefined,
            geometry.cornerApexSuggestions,
          );
        } else if (circuitDraftMatch) {
          geometry = geometryFromCircuitDraft(circuitDraftMatch.payload);
          cornerRestore = resolveDraftCornerRestore(
            circuitDraftMatch.payload.cornerApexSuggestions as CornerApexSuggestion[] | undefined,
            geometry.cornerApexSuggestions,
          );
        } else {
          const geometryFraction =
            restoredStartLine && sourcePoly.length >= 2
              ? startFractionForStartLine(sourcePoly, restoredStartLine)
              : (restoredFraction ?? 0);
          geometry = recalibrateTrackGeometry(sourcePoly, geometryFraction, {
            startLineSuggestion: restoredStartLine ?? undefined,
          });
        }

        skipGeometryRecalcRef.current = true;

        setSession(loadedSession);
        setResult(pipeline);
        setWorkingTrackIdOverride(draft?.trackId ?? circuitDraftMatch?.trackId ?? null);
        setSourcePolylineOverride(
          draft?.payload.gpsPolyline ??
            circuitDraftMatch?.payload.gpsPolyline ??
            null,
        );
        setCalibratedGeometry(geometry);
        setStartLineFraction(
          draft?.payload.startFraction ??
            circuitDraftMatch?.payload.startFraction ??
            null,
        );
        setCustomStartLine(
          draft?.payload.startLineSuggestion ??
            circuitDraftMatch?.payload.startLineSuggestion ??
            null,
        );
        const matchedContribution = circuitDraftMatch
          ? allTeamContributions.find((contribution) => contribution.id === circuitDraftMatch.draftId) ?? null
          : null;
        const restoredStatus =
          draft?.status ?? matchedContribution?.status ?? pipeline.contribution.status;

        setManualCorners(cornerRestore.manualCorners);
        setContributionStatus(restoredStatus);
        if (restoredStatus === 'merged' || restoredStatus === 'accepted') {
          setDataLayer('global');
        }
        setDraftCircuitName(
          draft?.venue?.trim() ||
            circuitDraftMatch?.circuitName ||
            pipeline.matchedTrack.name,
        );

        if (draft) {
          setActiveDraftId(draft.id);
          let notice = `Loaded draft from ${new Date(draft.updatedAt).toLocaleString()}`;
          if (draft.trackId !== trackId) {
            notice += ' — re-save to attach this draft to your team track profile';
          }
          if (cornerRestore.skippedPartialDraft) {
            notice += ` — ignored ${cornerRestore.draftCornerCount} saved turn(s); restored ${geometry.cornerApexSuggestions.length} auto-detected turns. Click Save draft to overwrite.`;
          }
          setDraftNotice(notice);
        } else if (circuitDraftMatch) {
          setActiveDraftId(circuitDraftMatch.draftId);
          setDraftNotice(
            `Matched saved circuit "${circuitDraftMatch.circuitName}" (${Math.round(circuitDraftMatch.confidence * 100)}%) by GPS shape + ${circuitDraftMatch.direction}. Laps resliced at that circuit's start/finish.`,
          );
        } else if (token) {
          setDraftNotice('No saved draft for this session yet.');
        }
      } catch (loadError) {
        setSession(null);
        setResult(null);
        setError(loadError instanceof Error ? loadError.message : 'Failed to load session');
      } finally {
        setLoading(false);
        skipGeometryRecalcRef.current = false;
      }
    },
    [applyMapLayout, token],
  );

  useEffect(() => {
    if (result || loading || dataFilesLoading || mapLayoutBootstrapRef.current) {
      return;
    }
    if (!initialSessionLoadedRef.current || sessionKey || sessionOptions.length > 0) {
      return;
    }

    const trackId = mapLayoutTrackId || trackPickerOptions[0]?.id;
    if (!trackId) {
      return;
    }

    mapLayoutBootstrapRef.current = true;
    startMapLayout(
      trackId,
      mapViewport ?? DEFAULT_MAP_CENTER,
      mapViewport?.label ?? lookupTrackLabel(trackId, teamTracks),
    );
  }, [
    dataFilesLoading,
    loading,
    mapLayoutTrackId,
    mapViewport,
    result,
    sessionKey,
    sessionOptions.length,
    startMapLayout,
    teamTracks,
    trackPickerOptions,
  ]);

  useEffect(() => {
    if (dataFilesLoading || initialSessionLoadedRef.current) {
      return;
    }

    initialSessionLoadedRef.current = true;

    const isGoProSource =
      requestedSource === 'gopro' || isGoProEnrichmentSessionKey(requestedSessionKey);

    if (isGoProSource && requestedSessionKey) {
      const stashed = loadStashedGoProEnrichmentSession(requestedSessionKey);
      if (!stashed) {
        setLoading(false);
        setError('GoPro session expired. Open circuit editor again from the GoPro upload preview.');
        return;
      }

      void loadSession(
        {
          key: requestedSessionKey,
          title: stashed.fileName,
          subtitle: 'GoPro GPS trace',
          loadUrl: '',
        },
        {
          draftId: requestedDraftId ?? undefined,
          sessionExport: stashed.session,
        },
      );
      return;
    }

    const initialOption =
      (requestedSessionKey
        ? sessionOptions.find((option) => option.key === requestedSessionKey)
        : null) ?? pickInitialTelemetrySession(dataFilesPage?.data ?? []);

    if (!initialOption) {
      setLoading(false);
      if (token) {
        void fetchTelemetryTracks()
          .then((tracks) => {
            setTeamTracks(tracks);
            if (tracks[0]) {
              setMapLayoutTrackId(tracks[0].id);
            }
          })
          .catch(() => undefined);
      }
      setError(null);
      return;
    }

    void loadSession(initialOption, {
      draftId: requestedDraftId ?? undefined,
    });
  }, [
    dataFilesLoading,
    dataFilesPage?.data,
    loadSession,
    requestedDraftId,
    requestedSessionKey,
    requestedSource,
    sessionOptions,
  ]);

  const sourcePolyline = useMemo(
    () => sourcePolylineOverride ?? result?.contribution.payload.gpsPolyline ?? [],
    [result, sourcePolylineOverride],
  );

  const applyDraft = useCallback((draft: TrackContribution) => {
    const geometry = geometryFromCircuitDraft(draft.payload);
    const cornerRestore = resolveDraftCornerRestore(
      draft.payload.cornerApexSuggestions as CornerApexSuggestion[] | undefined,
      geometry.cornerApexSuggestions,
    );
    skipGeometryRecalcRef.current = true;

    setCalibratedGeometry(geometry);
    setWorkingTrackIdOverride(draft.trackId);
    setSourcePolylineOverride(draft.payload.gpsPolyline);
    setStartLineFraction(draft.payload.startFraction ?? null);
    setCustomStartLine(draft.payload.startLineSuggestion ?? null);
    setManualCorners(cornerRestore.manualCorners);
    if (isManualMapSessionKey(draft.sessionKey) && draft.payload.gpsPolyline.length >= 2) {
      setManualLapPolyline(draft.payload.gpsPolyline);
      setLapDrawClosed(isManualLapDrawComplete(draft.payload.gpsPolyline, true));
      setMapLayoutMode(true);
      setEditMode('none');
    }
    setActiveDraftId(draft.id);
    setContributionStatus('draft');
    if (draft.venue?.trim()) {
      setDraftCircuitName(draft.venue.trim());
    }
    let notice = `Loaded draft from ${new Date(draft.updatedAt).toLocaleString()}`;
    if (cornerRestore.skippedPartialDraft) {
      notice += ` — ignored ${cornerRestore.draftCornerCount} saved turn(s); restored ${geometry.cornerApexSuggestions.length} auto-detected turns. Click Save draft to overwrite.`;
    }
    setDraftNotice(notice);
    skipGeometryRecalcRef.current = false;
  }, []);

  const handleReloadDraft = useCallback(async () => {
    if (!result || !token) {
      setDraftNotice('Sign in to load drafts.');
      return;
    }

    if (!activeDraftId) {
      setDraftNotice('Select a saved draft or save this one first.');
      return;
    }

    setDraftNotice('Loading draft…');

    try {
      const legacyTrackId = session ? resolveCatalogTrackId(session) : null;
      const draft = await fetchTrackContributionDraftForSession(result.contribution.payload.trackId, sessionKey ?? '', {
        draftId: activeDraftId,
        fallbackTrackIds:
          legacyTrackId && legacyTrackId !== result.contribution.payload.trackId ? [legacyTrackId] : [],
      });

      if (!draft) {
        setDraftNotice('Draft not found.');
        setActiveDraftId(null);
        return;
      }

      applyDraft(draft);
    } catch (loadError) {
      setDraftNotice(loadError instanceof Error ? loadError.message : 'Failed to load draft');
    }
  }, [activeDraftId, applyDraft, result, session, sessionKey, token]);

  useEffect(() => {
    if (!result || skipGeometryRecalcRef.current) {
      return;
    }

    if (isMapLayoutMode) {
      const center =
        mapViewport ??
        result.location ??
        result.contribution.payload.gpsPolyline[0] ??
        null;
      if (!center) {
        return;
      }

      const corners = isMapLayoutMode
        ? (manualCorners ?? [])
        : (manualCorners ?? result.contribution.payload.cornerApexSuggestions ?? []);
      setCalibratedGeometry(
        buildManualTrackGeometry({
          mapCenter: center,
          startLine: customStartLine,
          corners,
          includeSectorMarkers: showSectorMarkers,
          drawnPolyline: manualLapPolyline,
          drawnPolylineClosed: lapDrawClosed,
        }),
      );
      return;
    }

    if (sourcePolyline.length < 2) {
      return;
    }

    const fraction = customStartLine
      ? startFractionForStartLine(sourcePolyline, customStartLine)
      : (startLineFraction ?? 0);
    setCalibratedGeometry(
      recalibrateTrackGeometry(sourcePolyline, fraction, {
        startLineSuggestion: customStartLine ?? undefined,
      }),
    );
  }, [
    customStartLine,
    isMapLayoutMode,
    lapDrawClosed,
    manualCorners,
    manualLapPolyline,
    mapViewport,
    result,
    showSectorMarkers,
    sourcePolyline,
    startLineFraction,
  ]);

  const activeGeometry = calibratedGeometry;

  const activeGeometryForSave = useMemo(() => {
    if (!activeGeometry) {
      return null;
    }
    if (showSectorMarkers) {
      return activeGeometry;
    }
    return { ...activeGeometry, segmentSuggestions: [] };
  }, [activeGeometry, showSectorMarkers]);

  const reslicedLaps = useMemo(() => {
    if (!session || !activeGeometry) {
      return { laps: [], crossingsMs: [] };
    }

    return resliceLapsAtStartLine(session, activeGeometry.startLineSuggestion, {
      referencePolyline: activeGeometry.polyline,
    });
  }, [activeGeometry, lapResliceGeneration, session]);

  const flyingReslicedLaps = useMemo(() => reslicedFlyingLaps(reslicedLaps), [reslicedLaps]);
  const resliceKindCounts = useMemo(() => reslicedLapKindCounts(reslicedLaps), [reslicedLaps]);
  const selectedReslicedLap = useMemo(
    () => reslicedLaps.laps.find((lap) => lap.lapNumber === selectedReslicedLapNumber) ?? null,
    [reslicedLaps.laps, selectedReslicedLapNumber],
  );

  const lapReplayTimeline = useMemo(() => {
    if (reslicedLaps.laps.length === 0) {
      return null;
    }

    return buildReslicedLapReplayTimeline(reslicedLaps, selectedReslicedLapNumber);
  }, [reslicedLaps, selectedReslicedLapNumber]);

  const sessionReplayTimeline = useMemo(() => buildReslicedSessionReplayTimeline(reslicedLaps), [reslicedLaps]);

  const replayTimeline = useMemo(() => {
    if (replayScope === 'session') {
      return sessionReplayTimeline;
    }

    return lapReplayTimeline;
  }, [lapReplayTimeline, replayScope, sessionReplayTimeline]);

  const replay = useTelemetryReplay(replayTimeline);

  const highlightedReslicedLapNumber =
    replayScope === 'session'
      ? replay.activeLapNumber ?? selectedReslicedLapNumber
      : selectedReslicedLapNumber;

  const highlightedReslicedLap = useMemo(
    () => reslicedLaps.laps.find((lap) => lap.lapNumber === highlightedReslicedLapNumber) ?? selectedReslicedLap,
    [highlightedReslicedLapNumber, reslicedLaps.laps, selectedReslicedLap],
  );

  const replayLapScopeLabel = highlightedReslicedLap ? formatReslicedLapLabel(highlightedReslicedLap) : null;

  const handleRegenerateLaps = useCallback(() => {
    if (!result || sourcePolyline.length < 2) {
      return;
    }

    const fraction = startLineFraction ?? 0;
    setCalibratedGeometry(recalibrateTrackGeometry(sourcePolyline, fraction));
    preferFlyingLapRef.current = true;
    setLapResliceGeneration((value) => value + 1);
    setReplayScope('lap');
    replay.pause();
    replay.restart();
  }, [replay, result, sourcePolyline, startLineFraction]);

  useEffect(() => {
    preferFlyingLapRef.current = true;
  }, [activeGeometry?.startFraction]);

  useEffect(() => {
    if (!preferFlyingLapRef.current || flyingReslicedLaps.length === 0) {
      return;
    }

    setSelectedReslicedLapNumber(flyingReslicedLaps[0]!.lapNumber);
    preferFlyingLapRef.current = false;
  }, [flyingReslicedLaps]);

  useEffect(() => {
    if (reslicedLaps.laps.length === 0) {
      return;
    }

    const selected = reslicedLaps.laps.find((lap) => lap.lapNumber === selectedReslicedLapNumber);
    if (!selected) {
      setSelectedReslicedLapNumber(flyingReslicedLaps[0]?.lapNumber ?? reslicedLaps.laps[0]!.lapNumber);
    }
  }, [flyingReslicedLaps, reslicedLaps, selectedReslicedLapNumber]);

  const replayMapPointsRaw = useMemo(() => {
    if (!session || !replayTimeline) {
      return [];
    }

    return buildMapPointsForGpsWindow(
      session,
      replayTimeline.startTimecodeMs,
      replayTimeline.endTimecodeMs,
      REPLAY_MAP_MAX_POINTS,
    );
  }, [replayScope, replayTimeline, session]);

  const replayCursors = useMemo(() => {
    if (replayMapPointsRaw.length < 2) {
      return [];
    }

    return [
      {
        id: 'resliced-lap',
        points: replayMapPointsRaw,
        timecodeMs: replay.playheadTimecodeMs,
        color: '#A855F7',
        label:
          replayScope === 'session'
            ? 'Session'
            : selectedReslicedLap
              ? formatReslicedLapLabel(selectedReslicedLap)
              : `L${selectedReslicedLapNumber}`,
        follow: true,
      },
    ];
  }, [replay.playheadTimecodeMs, replayMapPointsRaw, replayScope, selectedReslicedLap, selectedReslicedLapNumber]);

  const selectedLapPolyline = useMemo(() => {
    if (!session || !selectedReslicedLap) {
      return [] as GpsCoord[];
    }
    return buildMapPointsForGpsWindow(
      session,
      selectedReslicedLap.startMs,
      selectedReslicedLap.endMs,
      REPLAY_MAP_MAX_POINTS,
    ).map((point) => ({ lat: point.lat, lon: point.lon }));
  }, [selectedReslicedLap, session]);

  const displayCorners = useMemo(() => {
    if (!activeGeometry) {
      return [];
    }
    if (isMapLayoutMode) {
      return manualCorners ?? [];
    }
    return manualCorners ?? activeGeometry.cornerApexSuggestions;
  }, [activeGeometry, isMapLayoutMode, manualCorners]);

  const layoutDirection = useMemo(() => {
    if (!isMapLayoutMode || !lapDrawClosed || manualLapPolyline.length < 3) {
      return null;
    }
    return detectDirection(healCircuitPolylineForDisplay(manualLapPolyline));
  }, [isMapLayoutMode, lapDrawClosed, manualLapPolyline]);

  const layoutDirectionPoints = useMemo(() => {
    if (!layoutDirection) {
      return [] as TrackMapPoint[];
    }
    return coordsToMapPoints(healCircuitPolylineForDisplay(manualLapPolyline), 82);
  }, [layoutDirection, manualLapPolyline]);

  const lapDrawComplete = isManualLapDrawComplete(manualLapPolyline, lapDrawClosed);

  const setEditModeSafe = useCallback((mode: EditMode) => {
    setEditMode(mode);
    if (mode !== 'move-turn') {
      setSelectedCornerId(null);
    }
    if (mode !== 'draw-lap') {
      setSelectedLapVertexIndex(null);
    }
    if (mode !== 'place-sf') {
      setStartLineDrawAnchor(null);
    }
  }, []);

  const turnMarkersInteractive =
    (!isMapLayoutMode || lapDrawComplete) && editMode !== 'place-sf' && editMode !== 'draw-lap';
  const lapVertexMarkersInteractive = isMapLayoutMode && !lapDrawClosed && manualLapPolyline.length > 0;
  const mapMarkersInteractive = turnMarkersInteractive || lapVertexMarkersInteractive;
  const turnPlacementPolyline =
    !isMapLayoutMode && selectedLapPolyline.length >= 2
      ? selectedLapPolyline
      : (activeGeometry?.polyline ?? []);
  const snapCornersToPolyline = turnPlacementPolyline.length >= 2;

  const handleCloseLapDraw = useCallback(() => {
    if (manualLapPolyline.length < 3) {
      setDraftNotice('Add at least 3 points before closing the lap.');
      return;
    }

    setManualLapPolyline(closeManualLapDrawPolyline(manualLapPolyline));
    setLapDrawClosed(true);
    setEditModeSafe('none');
    const direction = detectDirection(healCircuitPolylineForDisplay(closeManualLapDrawPolyline(manualLapPolyline)));
    setDraftNotice(
      `Lap closed · ${direction} · click Turn, then click each corner on your yellow line. Then use S/F for start/finish.`,
    );
  }, [manualLapPolyline, setEditModeSafe]);

  const handleUndoLapPoint = useCallback(() => {
    setManualLapPolyline((current) => undoManualLapDrawPoint(current));
    setLapDrawClosed(false);
    setEditModeSafe('draw-lap');
  }, [setEditModeSafe]);

  const handleResetLapDraw = useCallback(() => {
    setManualLapPolyline([]);
    setLapDrawClosed(false);
    setStartLineFraction(null);
    setCustomStartLine(null);
    setStartLineDrawAnchor(null);
    setManualCorners(null);
    setSelectedCornerId(null);
    setSelectedLapVertexIndex(null);
    setEditModeSafe('draw-lap');
    setDraftNotice('Trace the circuit outline on the map.');
  }, [setEditModeSafe]);

  const handleMapInteraction = useCallback(
    (coord: { lat: number; lon: number }) => {
      if (!activeGeometry) {
        return;
      }

      if (editMode === 'draw-lap') {
        setSelectedLapVertexIndex(null);
        const next = appendManualLapDrawPoint(manualLapPolyline, coord);
        setManualLapPolyline(next.polyline);
        setLapDrawClosed(next.closed);
        if (next.closed) {
          setEditModeSafe('none');
          const direction = detectDirection(healCircuitPolylineForDisplay(next.polyline));
          setDraftNotice(
            `Lap closed · ${direction} · click Turn, then click each corner on your yellow line. Then use S/F for start/finish.`,
          );
        } else if (next.polyline.length === 1) {
          setDraftNotice('Point 1 placed — keep clicking along the racing line. Purple dashed lines are OSM reference only.');
        } else {
          setDraftNotice(
            `${next.polyline.length} points · click near point 1 or Close lap when the outline looks right.`,
          );
        }
        return;
      }

      if (editMode === 'place-sf') {
        if (!startLineDrawAnchor) {
          setStartLineDrawAnchor(coord);
          setDraftNotice('Click the opposite side of the track to finish the start/finish line.');
          return;
        }

        const startLine = buildStartLineFromEndpoints(startLineDrawAnchor, coord);
        if (startLineLengthM(startLine) < 6) {
          setDraftNotice('Line is too short — click farther apart across the track.');
          setStartLineDrawAnchor(null);
          return;
        }

        setStartLineDrawAnchor(null);
        setCustomStartLine(startLine);
        setDraftNotice(null);
        setEditModeSafe('none');

        if (snapCornersToPolyline) {
          const fraction = startFractionForStartLine(sourcePolyline, startLine);
          setStartLineFraction(fraction);
        } else {
          setStartLineFraction(0);
        }
        return;
      }

      const baseCorners = isMapLayoutMode
        ? (manualCorners ?? [])
        : (manualCorners ?? activeGeometry.cornerApexSuggestions);
      const cornerOptions = { snapToPolyline: snapCornersToPolyline };

      if (editMode === 'add-turn') {
        const next = insertCornerAtCoord(baseCorners, coord, turnPlacementPolyline, cornerOptions);
        setManualCorners(next);
        setDraftNotice(`${next.length} turns marked · keep clicking corners, then choose Done adding.`);
        return;
      }

      if (editMode === 'move-turn' && selectedCornerId) {
        setManualCorners(
          moveCornerToCoord(baseCorners, selectedCornerId, coord, turnPlacementPolyline, cornerOptions),
        );
      }
    },
    [
      activeGeometry,
      editMode,
      isMapLayoutMode,
      manualCorners,
      manualLapPolyline,
      selectedCornerId,
      setEditModeSafe,
      snapCornersToPolyline,
      sourcePolyline,
      startLineDrawAnchor,
      turnPlacementPolyline,
    ],
  );

  const handleMapHover = useCallback(
    (coord: GpsCoord | null) => {
      if (editMode !== 'add-turn' || !coord || !activeGeometry) {
        setTurnHoverCoord(null);
        return;
      }

      const preview = snapCornersToPolyline
        ? snapCoordToPolyline(turnPlacementPolyline, coord)
        : coord;
      setTurnHoverCoord((current) =>
        current && current.lat === preview.lat && current.lon === preview.lon
          ? current
          : preview,
      );
    },
    [activeGeometry, editMode, snapCornersToPolyline, turnPlacementPolyline],
  );

  useEffect(() => {
    if (editMode !== 'add-turn') {
      setTurnHoverCoord(null);
    }
  }, [editMode]);

  const handleMarkerClick = useCallback(
    (markerId: string) => {
      if (markerId.startsWith('lap-vertex-')) {
        const index = Number.parseInt(markerId.slice('lap-vertex-'.length), 10);
        if (!Number.isFinite(index)) {
          return;
        }
        setSelectedLapVertexIndex(index);
        setSelectedCornerId(null);
        setEditMode('draw-lap');
        return;
      }

      if (!markerId.startsWith('corner-')) {
        return;
      }
      setSelectedCornerId(markerId);
      setSelectedLapVertexIndex(null);
      setEditMode('move-turn');
    },
    [],
  );

  const handleDeleteSelectedLapVertex = useCallback(() => {
    if (selectedLapVertexIndex === null) {
      return;
    }

    setManualLapPolyline((current) =>
      removeManualLapDrawPointAtIndex(current, selectedLapVertexIndex),
    );
    setLapDrawClosed(false);
    setSelectedLapVertexIndex(null);
    setEditModeSafe('draw-lap');
  }, [selectedLapVertexIndex, setEditModeSafe]);

  const handleMarkerDragEnd = useCallback(
    (markerId: string, coord: { lat: number; lon: number }) => {
      if (!activeGeometry || !markerId.startsWith('corner-')) {
        return;
      }
      const baseCorners = isMapLayoutMode
        ? (manualCorners ?? [])
        : (manualCorners ?? activeGeometry.cornerApexSuggestions);
      setSelectedCornerId(markerId);
      setEditMode('move-turn');
      setManualCorners(
        moveCornerToCoord(baseCorners, markerId, coord, turnPlacementPolyline, {
          snapToPolyline: snapCornersToPolyline,
        }),
      );
    },
    [activeGeometry, isMapLayoutMode, manualCorners, snapCornersToPolyline, turnPlacementPolyline],
  );

  const handleDeleteSelectedCorner = useCallback(() => {
    if (!activeGeometry || !selectedCornerId) {
      return;
    }
    const baseCorners = manualCorners ?? activeGeometry.cornerApexSuggestions;
    const next = removeCornerById(baseCorners, selectedCornerId);
    setManualCorners(next);
    setSelectedCornerId(null);
    setEditModeSafe('none');
  }, [activeGeometry, manualCorners, selectedCornerId, setEditModeSafe]);

  const handleResetCorners = useCallback(() => {
    setManualCorners(null);
    setSelectedCornerId(null);
    setEditModeSafe('none');
  }, [setEditModeSafe]);

  const trackId = workingTrackIdOverride ?? result?.contribution.payload.trackId ?? null;

  const handleCreateTrackFromSelectedLap = useCallback(async () => {
    if (!token) {
      setDraftNotice('Sign in to create a separate track.');
      return;
    }
    if (!session || !selectedReslicedLap) {
      setDraftNotice('Select a lap first.');
      return;
    }

    const lapPoints = buildMapPointsForGpsWindow(
      session,
      selectedReslicedLap.startMs,
      selectedReslicedLap.endMs,
      REPLAY_MAP_MAX_POINTS,
    );
    const lapPolyline = lapPoints.map((point) => ({ lat: point.lat, lon: point.lon }));
    if (lapPolyline.length < 3) {
      setDraftNotice('That lap does not contain enough GPS points to create a track.');
      return;
    }

    setCreatingTrackFromLap(true);
    try {
      const center = lapPolyline[Math.floor(lapPolyline.length / 2)]!;
      const baseName = draftCircuitName.trim() || result?.matchedTrack.name || 'New track';
      const created = await createTelemetryTrack({
        name: `${baseName} · Lap ${selectedReslicedLap.lapNumber}`,
        address: null,
        centerLat: center.lat,
        centerLon: center.lon,
      });
      const geometry = recalibrateTrackGeometry(lapPolyline, 0);

      skipGeometryRecalcRef.current = true;
      setTeamTracks((current) => [...current.filter((candidate) => candidate.id !== created.id), created]);
      setWorkingTrackIdOverride(created.id);
      setSourcePolylineOverride(lapPolyline);
      setCalibratedGeometry(geometry);
      setStartLineFraction(0);
      setCustomStartLine(geometry.startLineSuggestion);
      setStartLineDrawAnchor(null);
      setManualCorners(null);
      setActiveDraftId(null);
      setContributionStatus('pending');
      setDataLayer('draft');
      setDraftCircuitName(created.name);
      setEditMode('none');
      setSelectedCornerId(null);
      setDraftNotice(
        `Created “${created.name}” from lap ${selectedReslicedLap.lapNumber}. The previous track is untouched; save this as a new draft when ready.`,
      );
      skipGeometryRecalcRef.current = false;
    } catch (createError) {
      setDraftNotice(createError instanceof Error ? createError.message : 'Failed to create track from lap');
    } finally {
      setCreatingTrackFromLap(false);
    }
  }, [draftCircuitName, result?.matchedTrack.name, selectedReslicedLap, session, token]);
  const catalogTrackId = useMemo(
    () => (session ? resolveCatalogTrackId(session) : null),
    [session],
  );
  const draftTrackIds = useMemo(() => {
    if (!trackId) {
      return [];
    }

    return catalogTrackId && catalogTrackId !== trackId ? [trackId, catalogTrackId] : [trackId];
  }, [catalogTrackId, trackId]);

  const { data: trackDrafts = [], mutate: mutateTrackDrafts, isLoading: trackDraftsLoading } = useSWR(
    token && draftTrackIds.length > 0 ? trackContributionDraftsListKey(draftTrackIds.join(':')) : null,
    () => fetchTrackContributionDraftsForTracks(draftTrackIds),
  );

  const selectedDraft = useMemo(
    () => trackDrafts.find((draft) => draft.id === activeDraftId) ?? null,
    [activeDraftId, trackDrafts],
  );

  const handleNewDraft = useCallback(() => {
    if (!result || sourcePolyline.length < 2) {
      return;
    }

    skipGeometryRecalcRef.current = true;
    const geometry = recalibrateTrackGeometry(sourcePolyline, 0);
    setCalibratedGeometry(geometry);
    setStartLineFraction(null);
    setCustomStartLine(null);
    setStartLineDrawAnchor(null);
    setManualCorners(null);
    setActiveDraftId(null);
    setContributionStatus('pending');
    setDraftCircuitName(result.venue !== 'Unknown' ? result.venue : result.matchedTrack.name);
    setDraftNotice('New unsaved draft — save to keep your changes.');
    setEditMode('none');
    setSelectedCornerId(null);
    skipGeometryRecalcRef.current = false;
  }, [result, sourcePolyline]);

  const handleSelectDraft = useCallback(
    async (draft: TrackContribution | null) => {
      if (!draft) {
        handleNewDraft();
        return;
      }

      if (draft.sessionKey !== sessionKey) {
        const option = sessionOptions.find((entry) => entry.key === draft.sessionKey);
        if (option) {
          await loadSession(option, { draftId: draft.id });
          return;
        }

        setDraftNotice(`Draft session ${draft.sessionKey} is not in your uploaded files.`);
        return;
      }

      applyDraft(draft);
      setActiveDraftId(draft.id);
    },
    [applyDraft, handleNewDraft, loadSession, sessionKey, sessionOptions],
  );

  const handleResetStartLine = useCallback(() => {
    setStartLineFraction(null);
    setCustomStartLine(null);
    setStartLineDrawAnchor(null);
    setManualCorners(null);
    setEditModeSafe('none');
  }, [setEditModeSafe]);

  const handleSaveDraft = useCallback(async () => {
    if (!token) {
      setDraftNotice('Sign in to save drafts.');
      return;
    }

    if (!result || !activeGeometryForSave || !trackId) {
      setDraftNotice('Wait for the session to finish loading before saving.');
      return;
    }

    if (isMapLayoutMode && !lapDrawComplete) {
      setDraftNotice('Trace and close the lap outline before saving.');
      return;
    }

    setDraftSaving(true);
    setDraftNotice(null);

    try {
      const payload = buildContributionDraftPayload(
        trackId,
        activeGeometryForSave,
        displayCorners,
        result.contribution.payload.confidenceScore,
      );

      const draftIdForSave = activeDraftId ?? createTrackContributionDraftId();

      const saved = await saveTrackContributionDraft(trackId, {
        sessionKey: sessionKey ?? '',
        draftId: draftIdForSave,
        venue: draftCircuitName.trim() || result.venue,
        payload,
      });

      setActiveDraftId(saved.id);
      setContributionStatus('draft');
      setDraftNotice(`Draft saved ${new Date(saved.updatedAt).toLocaleTimeString()}`);
      void mutateTrackDrafts();
      void mutateSwr(teamTrackContributionsListKey('draft'));
    } catch (saveError) {
      setDraftNotice(saveError instanceof Error ? saveError.message : 'Failed to save draft');
    } finally {
      setDraftSaving(false);
    }
  }, [activeDraftId, activeGeometryForSave, displayCorners, draftCircuitName, isMapLayoutMode, lapDrawComplete, mutateTrackDrafts, result, sessionKey, token, trackId]);

  const circuitOutlineMapPoints = useMemo(() => {
    if (session && flyingReslicedLaps.length > 0) {
      const bestLap = flyingReslicedLaps.reduce((best, lap) =>
        lap.durationSec < best.durationSec ? lap : best,
      );
      const points = buildMapPointsForGpsWindow(
        session,
        bestLap.startMs,
        bestLap.endMs,
        REPLAY_MAP_MAX_POINTS,
      );
      if (points.length >= 2) {
        return points;
      }
    }

    if (session && selectedReslicedLap) {
      const points = buildMapPointsForGpsWindow(
        session,
        selectedReslicedLap.startMs,
        selectedReslicedLap.endMs,
        REPLAY_MAP_MAX_POINTS,
      );
      if (points.length >= 2) {
        return points;
      }
    }

    if (activeGeometry && activeGeometry.polyline.length >= 2) {
      return coordsToMapPoints(healCircuitPolylineForDisplay(activeGeometry.polyline), 92);
    }

    return [];
  }, [activeGeometry, flyingReslicedLaps, selectedReslicedLap, session]);

  const filteredPrivateMapGroups = useMemo(() => {
    if (circuitOutlineMapPoints.length >= 2) {
      return [circuitOutlineMapPoints];
    }

    return [];
  }, [circuitOutlineMapPoints]);

  const privateMapPoints = useMemo(
    () => pickPrimaryTrackGroup(filteredPrivateMapGroups),
    [filteredPrivateMapGroups],
  );

  const globalMapPoints = useMemo(() => {
    if (!result) {
      return [];
    }
    return coordsToMapPoints(healCircuitPolylineForDisplay(result.globalProfile.officialRevision.gpsPolyline), 68);
  }, [result]);

  const suggestionMapPoints = useMemo(() => {
    if (!showSuggestionOverlay || circuitOutlineMapPoints.length < 2) {
      return [];
    }

    return circuitOutlineMapPoints;
  }, [circuitOutlineMapPoints, showSuggestionOverlay]);

  const activeMapPoints = useMemo(() => {
    switch (dataLayer) {
      case 'draft':
        return privateMapPoints;
      case 'global':
        return globalMapPoints;
    }
  }, [dataLayer, globalMapPoints, privateMapPoints]);

  const overlayGroups = useMemo(() => {
    if (!result || dataLayer !== 'global' || !showSuggestionOverlay) {
      return undefined;
    }
    return [suggestionMapPoints];
  }, [dataLayer, result, showSuggestionOverlay, suggestionMapPoints]);

  const circuitOutlineGroups = useMemo(() => {
    return circuitOutlineMapPoints.length >= 2 ? [circuitOutlineMapPoints] : [];
  }, [circuitOutlineMapPoints]);

  const osmReferenceTrackGroups = useMemo(() => {
    if (!osmOverlayImport || !showOsmOverlay) {
      return [] as TrackMapPoint[][];
    }

    const enabled = new Set(osmEnabledLayoutIds);
    return deriveOsmTrackLayouts(osmOverlayImport)
      .filter(
        (layout) =>
          enabled.has(layout.id) &&
          layout.coords.length >= 2 &&
          (layout.source === 'osm-full-circuit' ||
            layout.source === 'osm-shortcut-layout' ||
            layout.source === 'osm-building' ||
            layout.source === 'osm-other'),
      )
      .map((layout) => coordsToMapPoints(layout.coords, 68));
  }, [osmEnabledLayoutIds, osmOverlayImport, showOsmOverlay]);

  const osmReferenceTrackGuideColors = useMemo(() => {
    if (!osmOverlayImport || !showOsmOverlay) {
      return [] as ReturnType<typeof osmOverlayGuideColorsForLayoutIndex>[];
    }

    const enabled = new Set(osmEnabledLayoutIds);
    let colorIndex = 0;
    return deriveOsmTrackLayouts(osmOverlayImport)
      .filter(
        (layout) =>
          enabled.has(layout.id) &&
          layout.coords.length >= 2 &&
          (layout.source === 'osm-full-circuit' ||
            layout.source === 'osm-shortcut-layout' ||
            layout.source === 'osm-building' ||
            layout.source === 'osm-other'),
      )
      .map(() => osmOverlayGuideColorsForLayoutIndex(colorIndex++));
  }, [osmEnabledLayoutIds, osmOverlayImport, showOsmOverlay]);

  const mapReferenceTrackGuideColors = useMemo(() => {
    const colors = [...osmReferenceTrackGuideColors];
    if (!isMapLayoutMode && circuitOutlineGroups.length > 0) {
      return [osmOverlayGuideColorsForLayoutIndex(0), ...colors];
    }
    return colors.length > 0 ? colors : undefined;
  }, [circuitOutlineGroups.length, isMapLayoutMode, osmReferenceTrackGuideColors]);

  const mapReferenceTrackGroups = useMemo(() => {
    const groups: TrackMapPoint[][] = [];
    if (!isMapLayoutMode && circuitOutlineGroups.length > 0) {
      groups.push(...circuitOutlineGroups);
    }
    if (osmReferenceTrackGroups.length > 0) {
      groups.push(...osmReferenceTrackGroups);
    }
    return groups;
  }, [circuitOutlineGroups, isMapLayoutMode, osmReferenceTrackGroups]);

  const replayTrackGroups = useMemo(() => {
    if (!session || replayMapPointsRaw.length < 2) {
      return overlayGroups;
    }

    if (replayScope === 'session' && replayTimeline) {
      return buildMapPointGroupsForReslicedLaps(session, replayTimeline.laps, REPLAY_MAP_MAX_POINTS);
    }

    if (selectedReslicedLap) {
      return buildMapPointGroupsForReslicedLaps(session, [selectedReslicedLap], REPLAY_MAP_MAX_POINTS);
    }

    return [replayMapPointsRaw];
  }, [
    overlayGroups,
    replayMapPointsRaw,
    replayScope,
    replayTimeline,
    selectedReslicedLap,
    session,
  ]);

  const layoutOutlineMapPoints = useMemo(() => {
    if (!isMapLayoutMode || manualLapPolyline.length < 2) {
      return [] as TrackMapPoint[][];
    }

    if (!showLayoutOutline) {
      return [];
    }

    const displayPoly = lapDrawClosed
      ? healCircuitPolylineForDisplay(manualLapPolyline)
      : manualLapPolyline;

    return [coordsToMapPoints(displayPoly, lapDrawClosed ? 92 : 88)];
  }, [isMapLayoutMode, lapDrawClosed, manualLapPolyline, showLayoutOutline]);

  const displayTrackGroups = useMemo(() => {
    if (isMapLayoutMode) {
      if (showLayoutOutline && layoutOutlineMapPoints.length > 0) {
        return layoutOutlineMapPoints;
      }
      return [];
    }

    if (replayMapPointsRaw.length >= 2) {
      return replayTrackGroups;
    }

    if (dataLayer !== 'global' && filteredPrivateMapGroups.length > 0) {
      return filteredPrivateMapGroups;
    }

    return overlayGroups;
  }, [
    dataLayer,
    filteredPrivateMapGroups,
    isMapLayoutMode,
    layoutOutlineMapPoints,
    overlayGroups,
    replayMapPointsRaw.length,
    replayTrackGroups,
    showLayoutOutline,
  ]);

  const mapPointsForView = useMemo(() => {
    if (isMapLayoutMode) {
      const center =
        mapViewport ??
        result?.location ??
        result?.contribution.payload.gpsPolyline[0] ??
        null;
      if (center) {
        return coordsToMapPoints([center], 0);
      }
      return [];
    }

    return replayMapPointsRaw.length >= 2 ? replayMapPointsRaw : privateMapPoints;
  }, [isMapLayoutMode, mapViewport, privateMapPoints, replayMapPointsRaw, result]);

  const showReplayLegend = !isMapLayoutMode && replayMapPointsRaw.length >= 2;
  const showLapTrace = !isMapLayoutMode && replayMapPointsRaw.length >= 2;
  const showLayoutLine =
    isMapLayoutMode &&
    showLayoutOutline &&
    layoutOutlineMapPoints.length > 0 &&
    (layoutOutlineMapPoints[0]?.length ?? 0) >= 2;

  const mapMarkers = useMemo(() => {
    if (!activeGeometry) {
      return [];
    }

    const projectionReference =
      activeMapPoints.length >= 2
        ? activeMapPoints.map((point, index) => ({
            lat: point.lat,
            lon: point.lon,
            alt: 0,
            speed: 0,
            timecodeMs: index * 100,
          }))
        : activeGeometry.polyline.map((coord, index) => ({
            lat: coord.lat,
            lon: coord.lon,
            alt: 0,
            speed: 0,
            timecodeMs: index * 100,
          }));

    const projectAt = (coord: GpsCoord) =>
      projectTrackPointsWithReferenceBounds(
        [{ lat: coord.lat, lon: coord.lon, alt: 0, speed: 0, timecodeMs: 0 }],
        projectionReference,
        1,
      )[0];

    const startLine = activeGeometry.startLineSuggestion;
    const startMid = {
      lat: (startLine.pointA.lat + startLine.pointB.lat) / 2,
      lon: (startLine.pointA.lon + startLine.pointB.lon) / 2,
    };

    const sectorMarkers = showSectorMarkers
      ? activeGeometry.segmentSuggestions
          .filter((segment) => segment.endFraction > 0 && segment.endFraction < 1)
          .map((segment) => {
            const point = fractionToPolylinePoint(activeGeometry.polyline, segment.endFraction);
            if (!point) {
              return null;
            }
            const projected = projectAt(point);
            return {
              id: `sector-${segment.id}`,
              kind: 'sector' as const,
              label: segment.id.toUpperCase(),
              lat: point.lat,
              lon: point.lon,
              x: projected?.x ?? 0,
              y: projected?.y ?? 0,
              color: '#A855F7',
            };
          })
          .filter((marker): marker is NonNullable<typeof marker> => marker !== null)
      : [];

    const lapVertexMarkers =
      isMapLayoutMode && !lapDrawClosed
        ? manualLapPolyline.map((point, index) => {
            const projected = projectAt(point);
            return {
              id: `lap-vertex-${index}`,
              kind: 'note' as const,
              label: index === 0 ? '1' : `${index + 1}`,
              lat: point.lat,
              lon: point.lon,
              x: projected?.x ?? 0,
              y: projected?.y ?? 0,
              color: '#38BDF8',
              selected: index === selectedLapVertexIndex,
            };
          })
        : [];

    const showStartFinishMarker = !isMapLayoutMode || lapDrawComplete;

    const showAnnotationMarkers = !isMapLayoutMode || lapDrawComplete;

    return [
      ...lapVertexMarkers,
      ...(startLineDrawAnchor
        ? [
            {
              id: 'sf-draw-anchor',
              kind: 'sector-boundary' as const,
              label: '1',
              lat: startLineDrawAnchor.lat,
              lon: startLineDrawAnchor.lon,
              x: projectAt(startLineDrawAnchor)?.x ?? 0,
              y: projectAt(startLineDrawAnchor)?.y ?? 0,
              color: '#22C55E',
              selected: true,
            },
          ]
        : []),
      ...(showStartFinishMarker
        ? [
            {
              id: 'start-line-mid',
              kind: 'sector-boundary' as const,
              label: 'S/F',
              lat: startMid.lat,
              lon: startMid.lon,
              x: projectAt(startMid)?.x ?? 0,
              y: projectAt(startMid)?.y ?? 0,
              color: '#22C55E',
              selected: !startLineDrawAnchor,
            },
          ]
        : []),
      ...(showAnnotationMarkers ? sectorMarkers : []),
      ...(showAnnotationMarkers
        ? displayCorners.map((corner) => {
            const projected = projectAt(corner);
            return {
              id: corner.id,
              kind: 'note' as const,
              label: `T${corner.turnNumber}`,
              lat: corner.lat,
              lon: corner.lon,
              x: projected?.x ?? 0,
              y: projected?.y ?? 0,
              color: '#F59E0B',
              selected: corner.id === selectedCornerId,
            };
          })
        : []),
    ];
  }, [
    activeGeometry,
    activeMapPoints,
    displayCorners,
    isMapLayoutMode,
    lapDrawClosed,
    lapDrawComplete,
    manualLapPolyline,
    selectedCornerId,
    selectedLapVertexIndex,
    showSectorMarkers,
    startLineDrawAnchor,
  ]);

  const handleApprove = useCallback(async () => {
    if (!token) {
      setDraftNotice('Sign in to publish circuits globally.');
      return;
    }

    if (!result || !activeGeometryForSave || !trackId) {
      setDraftNotice('Wait for the session to finish loading before publishing.');
      return;
    }

    if (isMapLayoutMode && !lapDrawComplete) {
      setDraftNotice('Trace and close the lap outline before publishing.');
      return;
    }

    setReviewSaving(true);
    setDraftNotice(null);

    try {
      const payload = buildContributionDraftPayload(
        trackId,
        activeGeometryForSave,
        displayCorners,
        result.contribution.payload.confidenceScore,
      );
      const draftIdForSave = activeDraftId ?? createTrackContributionDraftId();

      const saved = await saveTrackContribution(
        trackId,
        {
          sessionKey: sessionKey ?? '',
          draftId: draftIdForSave,
          venue: draftCircuitName.trim() || result.venue,
          payload,
        },
        'merged',
      );

      setActiveDraftId(saved.id);
      setContributionStatus('merged');
      setDataLayer('global');
      setDraftNotice(`Published globally ${new Date(saved.updatedAt).toLocaleTimeString()}`);
      setResult((current) => {
        if (!current) {
          return current;
        }

        const steps = current.steps.map((step) =>
          step.id === 'approve'
            ? { ...step, status: 'done' as const, detail: 'Published to global layer' }
            : step,
        );

        return {
          ...current,
          steps,
          contribution: { ...current.contribution, status: 'merged', payload },
          globalProfile: {
            ...current.globalProfile,
            officialRevision: payload,
            communitySuggestions: [
              ...current.globalProfile.communitySuggestions.filter((entry) => entry.id !== saved.id),
              {
                ...current.contribution,
                id: saved.id,
                status: 'merged',
                payload,
                venue: saved.venue ?? current.contribution.venue,
              },
            ],
          },
        };
      });

      void mutateTrackDrafts();
      void mutateSwr(teamTrackContributionsListKey('draft'));
    } catch (approveError) {
      setDraftNotice(approveError instanceof Error ? approveError.message : 'Failed to publish circuit');
    } finally {
      setReviewSaving(false);
    }
  }, [
    activeDraftId,
    activeGeometryForSave,
    displayCorners,
    draftCircuitName,
    isMapLayoutMode,
    lapDrawComplete,
    mutateTrackDrafts,
    result,
    sessionKey,
    token,
    trackId,
  ]);

  const handleReject = useCallback(async () => {
    if (!token) {
      setDraftNotice('Sign in to reject circuits.');
      return;
    }

    if (!result || !activeGeometry || !trackId) {
      setDraftNotice('Wait for the session to finish loading before rejecting.');
      return;
    }

    setReviewSaving(true);
    setDraftNotice(null);

    try {
      const payload = buildContributionDraftPayload(
        trackId,
        activeGeometry,
        displayCorners,
        result.contribution.payload.confidenceScore,
      );
      const draftIdForSave = activeDraftId ?? createTrackContributionDraftId();

      const saved = await saveTrackContribution(
        trackId,
        {
          sessionKey: sessionKey ?? '',
          draftId: draftIdForSave,
          venue: draftCircuitName.trim() || result.venue,
          payload,
        },
        'rejected',
      );

      setActiveDraftId(saved.id);
      setContributionStatus('rejected');
      setDraftNotice(`Rejected ${new Date(saved.updatedAt).toLocaleTimeString()}`);
      setResult((current) => {
        if (!current) {
          return current;
        }

        const steps = current.steps.map((step) =>
          step.id === 'approve'
            ? { ...step, status: 'skipped' as const, detail: 'Rejected — kept private' }
            : step,
        );

        return {
          ...current,
          steps,
          contribution: { ...current.contribution, status: 'rejected', payload },
        };
      });

      void mutateTrackDrafts();
      void mutateSwr(teamTrackContributionsListKey('draft'));
    } catch (rejectError) {
      setDraftNotice(rejectError instanceof Error ? rejectError.message : 'Failed to reject circuit');
    } finally {
      setReviewSaving(false);
    }
  }, [
    activeDraftId,
    activeGeometry,
    displayCorners,
    draftCircuitName,
    mutateTrackDrafts,
    result,
    sessionKey,
    token,
    trackId,
  ]);

  if (dataFilesLoading || (loading && !result)) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 360, px: 2 }}>
        <Stack spacing={2} alignItems="center" sx={{ width: '100%', maxWidth: 420 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            {dataFilesLoading
              ? 'Loading available sessions…'
              : `Loading ${activeSessionOption?.title ?? 'session'} and running enrichment pipeline…`}
          </Typography>
          <SessionPicker
            options={sessionOptions}
            value={selectedSessionOption}
            loading={dataFilesLoading}
            disabled
            onSelect={() => undefined}
          />
        </Stack>
      </Box>
    );
  }

  if (error?.startsWith('GoPro session expired')) {
    const fallbackSession = pickInitialTelemetrySession(dataFilesPage?.data ?? []);

    return (
      <Stack spacing={2} sx={{ maxWidth: 720 }}>
        <SessionPicker
          options={sessionOptions}
          value={activeSessionOption}
          disabled={loading}
          onSelect={(option) => {
            void loadSession(option);
          }}
        />
        <Alert
          severity="warning"
          action={
            <Stack direction="row" spacing={1}>
              {fallbackSession ? (
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setError(null);
                    void loadSession(fallbackSession);
                  }}
                >
                  Load MyChron
                </Button>
              ) : null}
              <Button color="inherit" size="small" href="/devtools?tool=gopro-footage">
                GoPro Footage
              </Button>
            </Stack>
          }
        >
          That temporary GoPro editor session expired. Reopen the GoPro upload from the GoPro Footage tab, or load a
          MyChron session here.
        </Alert>
      </Stack>
    );
  }

  if (!result) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 360, px: 2 }}>
        <Stack spacing={2} alignItems="center" sx={{ width: '100%', maxWidth: 420 }}>
          <CircularProgress />
          <Typography variant="body2" color="text.secondary" textAlign="center">
            Opening satellite map editor…
          </Typography>
        </Stack>
      </Box>
    );
  }

  const { contribution, matchedTrack, lapQualities, steps } = result;
  const sessionMeta = session?.metadata;
  const circuitDisplayName = draftCircuitName.trim() || matchedTrack.name;
  const pipelineDone = steps.filter((step) => step.status === 'done').length;
  const cornerCount = displayCorners.length;
  const selectedCorner = displayCorners.find((corner) => corner.id === selectedCornerId) ?? null;
  const rightInset = reviewOpen ? RIGHT_PANEL_WIDTH + PANEL_GAP * 2 : PANEL_GAP;
  const leftInset = detailsOpen ? LEFT_PANEL_WIDTH + PANEL_GAP * 2 : PANEL_GAP;
  const bottomDockReserve =
    reslicedLaps.laps.length > 0
      ? PANEL_GAP + LAP_STRIP_BASE_HEIGHT + 6 + REPLAY_BAR_HEIGHT
      : PANEL_GAP;

  const canResetEdits =
    startLineFraction !== null ||
    customStartLine ||
    manualCorners ||
    manualLapPolyline.length > 0;

  const layoutWorkflowSteps = isMapLayoutMode
    ? [
        {
          label: 'Trace the lap',
          detail: lapDrawComplete ? 'Done' : 'Draw mode — click the map along the racing line',
          done: lapDrawComplete,
          active: !lapDrawComplete,
        },
        {
          label: 'Mark turns',
          detail: lapDrawComplete
            ? 'Choose Add turns once, then click every corner on your yellow line'
            : 'Unlocks after the lap is closed',
          done: cornerCount > 0,
          active: lapDrawComplete && cornerCount === 0,
        },
        {
          label: 'Start / finish',
          detail: lapDrawComplete
            ? 'Click S/F, then click both edges of the track'
            : 'Unlocks after the lap is closed',
          done: Boolean(customStartLine),
          active: lapDrawComplete && !customStartLine,
        },
      ]
    : [];

  const layoutWorkflowPanel =
    layoutWorkflowSteps.length > 0 ? (
      <Stack spacing={0.75} sx={{ mb: 1 }}>
        {layoutWorkflowSteps.map((step) => (
          <Box
            key={step.label}
            sx={{
              px: 1,
              py: 0.75,
              borderRadius: 1,
              bgcolor: step.active
                ? 'rgba(56,189,248,0.12)'
                : step.done
                  ? 'rgba(34,197,94,0.08)'
                  : 'rgba(255,255,255,0.04)',
            }}
          >
            <Typography
              variant="caption"
              fontWeight={700}
              color={step.active ? 'info.main' : step.done ? 'success.main' : 'text.secondary'}
            >
              {step.done ? '✓ ' : step.active ? '→ ' : '○ '}
              {step.label}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.45 }}>
              {step.detail}
            </Typography>
          </Box>
        ))}
      </Stack>
    ) : null;

  const editControlsRow = (
    <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
      {reslicedLaps.laps.length > 1 ? (
        <>
          <Select
            size="small"
            value={selectedReslicedLapNumber}
            aria-label="Select lap to edit"
            renderValue={(value) => {
              const lap = reslicedLaps.laps.find((candidate) => candidate.lapNumber === value);
              return lap ? `Lap · ${formatReslicedLapLabel(lap)}` : `Lap ${value}`;
            }}
            onChange={(event) => {
              preferFlyingLapRef.current = false;
              setSelectedReslicedLapNumber(Number(event.target.value));
              setReplayScope('lap');
            }}
            sx={{
              minWidth: 132,
              height: 28,
              fontSize: '0.68rem',
              fontWeight: 700,
              '& .MuiSelect-select': { py: 0.35, px: 1 },
            }}
          >
            {reslicedLaps.laps.map((lap) => (
              <MenuItem key={lap.lapNumber} value={lap.lapNumber}>
                {formatReslicedLapLabel(lap)}
              </MenuItem>
            ))}
          </Select>
          <Button
            size="small"
            variant="outlined"
            disabled={creatingTrackFromLap || !selectedReslicedLap || !token}
            onClick={() => void handleCreateTrackFromSelectedLap()}
            sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
          >
            {creatingTrackFromLap ? 'Creating…' : 'New track from lap'}
          </Button>
        </>
      ) : null}
      <Button
        size="small"
        variant={mapLayoutMode ? 'contained' : 'outlined'}
        onClick={() => setMapLayoutMode((current) => !current)}
        sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
      >
        Map layout
      </Button>
      {isMapLayoutMode && !lapDrawComplete ? (
        <>
          <Button
            size="small"
            variant={editMode === 'draw-lap' ? 'contained' : 'outlined'}
            onClick={() => setEditModeSafe(editMode === 'draw-lap' ? 'none' : 'draw-lap')}
            sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
          >
            Draw
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={manualLapPolyline.length < 3}
            onClick={handleCloseLapDraw}
            sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
          >
            Close lap
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={manualLapPolyline.length === 0}
            onClick={handleUndoLapPoint}
            sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
          >
            Undo
          </Button>
        </>
      ) : null}
      {isMapLayoutMode && lapDrawComplete && editMode !== 'draw-lap' ? (
        <Button
          size="small"
          variant="outlined"
          onClick={() => {
            setManualLapPolyline(reopenManualLapDrawPolyline(manualLapPolyline));
            setLapDrawClosed(false);
            setEditModeSafe('draw-lap');
            setDraftNotice('Adjust the traced line, then close the lap again.');
          }}
          sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
        >
          Edit path
        </Button>
      ) : null}
      <Button
        size="small"
        variant={editMode === 'place-sf' ? 'contained' : 'outlined'}
        disabled={isMapLayoutMode && !lapDrawComplete}
        title={isMapLayoutMode && !lapDrawComplete ? 'Close the lap outline first' : undefined}
        onClick={() => setEditModeSafe(editMode === 'place-sf' ? 'none' : 'place-sf')}
        sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
      >
        S/F
      </Button>
      <Button
        size="small"
        variant={editMode === 'add-turn' ? 'contained' : 'outlined'}
        disabled={isMapLayoutMode && !lapDrawComplete}
        title={isMapLayoutMode && !lapDrawComplete ? 'Close the lap outline first' : undefined}
        onClick={() => setEditModeSafe(editMode === 'add-turn' ? 'none' : 'add-turn')}
        sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
      >
        {editMode === 'add-turn' ? 'Done adding' : 'Add turns'}
      </Button>
      <Button
        size="small"
        variant={showSectorMarkers ? 'contained' : 'outlined'}
        disabled={isMapLayoutMode && !lapDrawComplete}
        onClick={() => setShowSectorMarkers((current) => !current)}
        sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
      >
        Sectors
      </Button>
      {selectedLapVertexIndex !== null && lapVertexMarkersInteractive ? (
        <IconButton
          size="small"
          color="error"
          onClick={handleDeleteSelectedLapVertex}
          aria-label="Delete selected trace point"
          sx={{ width: 28, height: 28 }}
        >
          <DeleteOutlineIcon sx={{ fontSize: 16 }} />
        </IconButton>
      ) : null}
      {selectedCorner ? (
        <>
          <Chip
            size="small"
            color="warning"
            label={`Turn ${selectedCorner.turnNumber} selected`}
            sx={{ height: 28, fontSize: '0.68rem' }}
          />
          <Button
            size="small"
            color="error"
            variant="outlined"
            startIcon={<DeleteOutlineIcon sx={{ fontSize: 16 }} />}
            onClick={handleDeleteSelectedCorner}
            sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
          >
            Remove turn
          </Button>
        </>
      ) : null}
      <Button
        size="small"
        variant="outlined"
        disabled={!canResetEdits}
        onClick={() => {
          if (isMapLayoutMode && manualLapPolyline.length > 0) {
            handleResetLapDraw();
            return;
          }
          if (startLineFraction !== null || customStartLine) {
            handleResetStartLine();
          }
          if (manualCorners) {
            handleResetCorners();
          }
        }}
        sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
      >
        Reset trace
      </Button>
    </Stack>
  );

  const statusCaptionLine =
    editMode === 'draw-lap' ? (
      <Typography variant="caption" color="info.main" sx={{ display: 'block', lineHeight: 1.5 }}>
        {manualLapPolyline.length === 0
          ? 'Step 1 — click the map to place the first point on the racing line.'
          : lapDrawClosed
            ? layoutDirection
              ? `Lap closed · ${layoutDirection} · blue ticks show travel direction on the yellow line.`
              : 'Lap closed — use Turn and S/F next.'
            : selectedLapVertexIndex !== null
              ? `Point ${selectedLapVertexIndex + 1} selected — delete it or click the map to keep tracing.`
              : manualLapPolyline.length === 1
                ? 'Point 1 placed — keep clicking. Your line appears after point 2.'
                : `Tracing · ${manualLapPolyline.length} points — yellow line is your draft track. Close lap when ready.`}
      </Typography>
    ) : editMode === 'place-sf' ? (
      <Typography variant="caption" color="info.main" sx={{ display: 'block', lineHeight: 1.5 }}>
        {startLineDrawAnchor
          ? 'Click the opposite side of the track to draw the start/finish line across.'
          : 'Click one edge of the track, then the opposite edge to draw the start/finish line.'}
      </Typography>
    ) : editMode === 'move-turn' || editMode === 'add-turn' ? (
      <Typography variant="caption" color="info.main" sx={{ display: 'block', lineHeight: 1.5 }}>
        {editMode === 'move-turn'
          ? `Turn ${selectedCorner?.turnNumber ?? ''} selected — drag it, click a new position, or choose Remove turn.`
          : 'Add turns — keep clicking each corner on the track, then choose Done adding. Click an existing marker to move or remove it.'}
      </Typography>
    ) : (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', lineHeight: 1.5 }}>
        {isMapLayoutMode
          ? lapDrawComplete
            ? `${cornerCount} corners · ${Math.round(activeGeometry?.lapLengthEstimateM ?? 0)} m · ${layoutDirection ?? 'direction pending'}`
            : `${manualLapPolyline.length} trace points · finish step 1 first`
          : `${cornerCount} corners · ${Math.round(activeGeometry?.lapLengthEstimateM ?? 0)} m lap · S/F ${startLineFraction !== null ? `@ ${Math.round((startLineFraction ?? 0) * 100)}%` : 'auto'}`}
      </Typography>
    );

  const editorPanelBody = (
    <>
      <MapPlaceSearch onPlaceSelect={handlePlaceSelect} disabled={loading} />
      <input
        ref={osmFileInputRef}
        type="file"
        accept=".osm,application/xml,text/xml"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) {
            void handleOsmOverlayFile(file);
          }
        }}
      />
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, mb: 0.75 }} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          variant="outlined"
          onClick={() => osmFileInputRef.current?.click()}
          sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
        >
          Import OSM
        </Button>
        {osmOverlayImport ? (
          <>
            <Button
              size="small"
              variant={showOsmOverlay ? 'contained' : 'outlined'}
              onClick={() => setShowOsmOverlay((current) => !current)}
              sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
            >
              OSM overlay
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={handleClearOsmOverlay}
              sx={{ minWidth: 0, px: 1, fontSize: '0.68rem', py: 0.35, height: 28 }}
            >
              Clear OSM
            </Button>
          </>
        ) : null}
      </Stack>
      {osmOverlayImport ? (
        <Stack spacing={0.25} sx={{ mb: 1.25 }}>
          <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>
            OSM layouts
          </Typography>
          {deriveOsmTrackLayouts(osmOverlayImport)
            .filter(
              (layout) =>
                layout.source === 'osm-full-circuit' ||
                layout.source === 'osm-shortcut-layout' ||
                layout.source === 'osm-building' ||
                layout.source === 'osm-other',
            )
            .map((layout) => (
            <FormControlLabel
              key={layout.id}
              sx={{ m: 0, alignItems: 'flex-start' }}
              control={
                <Checkbox
                  size="small"
                  checked={osmEnabledLayoutIds.includes(layout.id)}
                  onChange={(_, checked) => {
                    setOsmEnabledLayoutIds((current) =>
                      checked ? [...current, layout.id] : current.filter((id) => id !== layout.id),
                    );
                    setShowOsmOverlay(true);
                  }}
                  sx={{ py: 0.25 }}
                />
              }
              label={
                <Typography variant="caption" sx={{ lineHeight: 1.45, display: 'block', pt: 0.65 }}>
                  {layout.label}
                </Typography>
              }
            />
          ))}
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.45 }}>
            Main and shortcut layouts are on by default. Shortcut layouts combine the full circuit with
            partial OSM segments at their junctions.
          </Typography>
        </Stack>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25, lineHeight: 1.45 }}>
          Import an OpenStreetMap `.osm` export to overlay reference outlines while tracing.
        </Typography>
      )}
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25, lineHeight: 1.45 }}>
        {mapViewport?.label ?? 'Search to jump the satellite map to a circuit or address.'}
      </Typography>
      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
        {circuitDisplayName} · {formatPercent(matchedTrack.confidence)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, lineHeight: 1.4 }}>
        Track match: {matchedTrack.method.replace('-', ' ')}
        {matchedTrack.id !== 'unknown' ? ` · ${matchedTrack.id}` : ''}
      </Typography>
      <FormControl size="small" fullWidth sx={{ mb: 1.5 }}>
        <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 0.5 }}>
          Track
        </Typography>
        <Select
          value={manualTrackId ?? 'auto'}
          displayEmpty
          onChange={(event) => {
            const nextValue = event.target.value;
            const nextManualTrackId = nextValue === 'auto' ? null : nextValue;
            setManualTrackId(nextManualTrackId);
            if (session && sessionKey) {
              rerunPipelineForTrack(session, sessionKey, nextManualTrackId);
              if (token && nextManualTrackId && nextManualTrackId !== 'unknown') {
                void updateTelemetrySessionContext(sessionKey, { trackId: nextManualTrackId }).then(() => {
                  setSessionPreferredTrackId(nextManualTrackId);
                });
              }
            }
          }}
        >
          <MenuItem value="auto">
            Auto-detect ({matchedTrack.name})
          </MenuItem>
          {trackPickerOptions.map((track) => (
            <MenuItem key={track.id} value={track.id}>
              {track.name}
              {track.source === 'catalog' ? ' · catalog' : ''}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <LinearProgress variant="determinate" value={matchedTrack.confidence * 100} sx={{ height: 4, borderRadius: 99, mb: 2 }} />

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25, lineHeight: 1.5 }}>
        Layer: {layerLabel(dataLayer)}
      </Typography>

      <Divider sx={{ my: SECTION_GAP }} />

      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
        Suggested improvements
      </Typography>
      <Stack spacing={1} sx={{ mb: SECTION_GAP }}>
        {contribution.findings.map((finding) => (
          <Box key={finding.id} sx={{ p: 1.25, borderRadius: 1.25, bgcolor: 'rgba(255,255,255,0.04)' }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" fontWeight={600}>
                {finding.title}
              </Typography>
              <Typography variant="caption">{formatPercent(finding.confidence)}</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', lineHeight: 1.45, mt: 0.25 }}>
              {finding.detail}
            </Typography>
          </Box>
        ))}
      </Stack>

      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
        Pipeline · {pipelineDone}/{steps.length}
      </Typography>
      <Stack spacing={0.65} sx={{ mb: SECTION_GAP }}>
        {steps.slice(0, 6).map((step) => (
          <Stack key={step.id} direction="row" spacing={0.75} alignItems="center">
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                flexShrink: 0,
                bgcolor:
                  step.status === 'done'
                    ? colors.success.main
                    : step.status === 'failed'
                      ? colors.error.main
                      : 'rgba(255,255,255,0.25)',
              }}
            />
            <Typography variant="caption" sx={{ fontSize: '0.65rem' }} noWrap>
              {step.label}
            </Typography>
          </Stack>
        ))}
        {steps.length > 6 ? (
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.62rem', pl: 1.75 }}>
            +{steps.length - 6} more steps
          </Typography>
        ) : null}
      </Stack>

      <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1 }}>
        Lap quality · {lapQualities.filter((lap) => lap.accepted).length} accepted
      </Typography>
      <Stack spacing={0.85}>
        {lapQualities.filter((lap) => lap.accepted).slice(0, 4).map((lap) => (
          <Box key={lap.lapNumber}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption">Lap {lap.lapNumber}</Typography>
              <Typography variant="caption" fontWeight={700}>
                {formatPercent(lap.qualityScore)}
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={lap.qualityScore * 100}
              color={lap.accepted ? 'success' : 'inherit'}
              sx={{ height: 3, borderRadius: 99 }}
            />
          </Box>
        ))}
      </Stack>
    </>
  );

  const circuitPanel = (
    <>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.25 }}>
        <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          Circuit
        </Typography>
        <Stack direction="row" spacing={0.5} alignItems="center">
          <StatusBadge
            status={
              contributionStatus === 'accepted' || contributionStatus === 'merged'
                ? 'positive'
                : contributionStatus === 'rejected'
                  ? 'needsWork'
                  : 'neutral'
            }
            label={contributionStatus}
          />
          <IconButton size="small" onClick={() => setReviewOpen(false)} aria-label="Collapse circuit panel">
            <ChevronRightIcon fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <TextField
        size="small"
        fullWidth
        label="Circuit name"
        placeholder={matchedTrack.name}
        value={draftCircuitName}
        onChange={(event) => setDraftCircuitName(event.target.value)}
        sx={{
          '& .MuiInputBase-root': { fontSize: '0.78rem' },
          '& .MuiInputLabel-root': { fontSize: '0.78rem' },
        }}
      />

      <ToggleButtonGroup
        exclusive
        size="small"
        value={dataLayer}
        onChange={(_, value: TrackDataLayer | null) => {
          if (value) {
            setDataLayer(value);
          }
        }}
        sx={{ ...layerToggleSx, mt: 1.25, width: '100%' }}
      >
        <ToggleButton value="draft" sx={{ flex: 1 }}>
          <EditOutlinedIcon sx={{ fontSize: 13 }} />
          Draft
        </ToggleButton>
        <ToggleButton value="global" sx={{ flex: 1 }}>
          <PublicOutlinedIcon sx={{ fontSize: 13 }} />
          Global
        </ToggleButton>
      </ToggleButtonGroup>

      {dataLayer === 'global' && circuitOutlineMapPoints.length >= 2 ? (
        <Button
          size="small"
          fullWidth
          variant={showSuggestionOverlay ? 'contained' : 'outlined'}
          onClick={() => setShowSuggestionOverlay((value) => !value)}
          sx={{ mt: 1, fontSize: '0.68rem', py: 0.55, textTransform: 'none' }}
        >
          {showSuggestionOverlay ? 'Hide draft overlay on map' : 'Show draft overlay on map'}
        </Button>
      ) : null}

      <Stack direction="row" spacing={0.75} sx={{ mt: 1.25 }}>
        <Button
          size="small"
          fullWidth
          variant="contained"
          color="secondary"
          startIcon={draftSaving ? <CircularProgress size={14} color="inherit" /> : <SaveOutlinedIcon sx={{ fontSize: 15 }} />}
          disabled={!activeGeometry || draftSaving || !token}
          onClick={() => void handleSaveDraft()}
          sx={{ fontSize: '0.7rem', py: 0.85 }}
        >
          {draftSaving ? 'Saving…' : 'Save'}
        </Button>
        <Button
          size="small"
          fullWidth
          variant="contained"
          color="success"
          startIcon={reviewSaving ? <CircularProgress size={14} color="inherit" /> : <CheckCircleOutlineIcon sx={{ fontSize: 15 }} />}
          disabled={
            reviewSaving
            || draftSaving
            || !token
            || contributionStatus === 'accepted'
            || contributionStatus === 'merged'
          }
          onClick={() => void handleApprove()}
          sx={{ fontSize: '0.7rem', py: 0.85, fontWeight: 700 }}
        >
          {reviewSaving ? 'Publishing…' : 'Publish'}
        </Button>
      </Stack>

      <Stack direction="row" spacing={0.75} sx={{ mt: 1, alignItems: 'flex-start' }}>
        <Autocomplete
          size="small"
          sx={{ flex: 1, minWidth: 0 }}
          options={trackDrafts}
          value={selectedDraft}
          loading={trackDraftsLoading}
          disabled={!token || !trackId}
          onChange={(_, value) => {
            void handleSelectDraft(value);
          }}
          getOptionLabel={(option) => formatTrackContributionDraftLabel(option)}
          isOptionEqualToValue={(left, right) => left.id === right.id}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={token ? 'Saved version…' : 'Sign in to load'}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {params.InputProps.endAdornment}
                    <Stack direction="row" spacing={0.25} sx={{ mr: -0.5 }}>
                      <IconButton
                        size="small"
                        aria-label="Reload saved version"
                        disabled={!result || !token || !activeDraftId}
                        onClick={() => void handleReloadDraft()}
                      >
                        <RefreshOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                      <IconButton
                        size="small"
                        aria-label="Start new draft"
                        disabled={!result}
                        onClick={handleNewDraft}
                      >
                        <RestartAltIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Stack>
                  </>
                ),
              }}
            />
          )}
        />
      </Stack>

      {draftNotice ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.45 }}>
          {draftNotice}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.45 }}>
          {cornerCount} turns · {Math.round(activeGeometry?.lapLengthEstimateM ?? 0)} m · {layerLabel(dataLayer)}
        </Typography>
      )}

      {contributionStatus !== 'merged' && contributionStatus !== 'rejected' ? (
        <Button
          size="small"
          color="error"
          disabled={reviewSaving || draftSaving}
          onClick={() => void handleReject()}
          sx={{ mt: 0.75, px: 0, minWidth: 0, fontSize: '0.68rem', textTransform: 'none' }}
        >
          Reject draft
        </Button>
      ) : null}
    </>
  );

  return (
    <Box
      sx={{
        position: 'relative',
        mx: { xs: -1.5, md: -2 },
        mt: { xs: -1.5, md: -2 },
        mb: { xs: -1.5, md: -2 },
        width: { xs: 'calc(100% + 24px)', md: 'calc(100% + 32px)' },
        height: { xs: 'calc(100vh - 24px)', md: 'calc(100vh - 32px)' },
        minHeight: 520,
        overflow: 'hidden',
      }}
    >
      <GpsSpeedTrackMap
        points={mapPointsForView}
        trackGroups={displayTrackGroups}
        referenceTrackGroups={mapReferenceTrackGroups}
        referenceTrackGuideColors={mapReferenceTrackGuideColors}
        referenceTrackStyle={osmReferenceTrackGroups.length > 0 ? 'guide' : 'default'}
        fill
        showLegend={showReplayLegend && reslicedLaps.laps.length === 0}
        showLapLines={showLapTrace || showLayoutLine}
        showIdealLine={layoutDirectionPoints.length >= 2}
        idealPoints={layoutDirectionPoints}
        heatLineWidth={showLayoutLine ? 7 : undefined}
        heatLineHaloWidth={showLayoutLine ? 12 : undefined}
        mapMarkers={mapMarkers}
        mapViewport={mapViewport ?? undefined}
        resetViewKey={`${dataLayer}-${startLineFraction ?? 'default'}-${selectedReslicedLapNumber}-${replayScope}-${lapResliceGeneration}-${isMapLayoutMode ? 'layout' : 'trace'}-${mapViewport?.lat ?? 0}-${mapViewport?.lon ?? 0}-${osmEnabledLayoutIds.join('.')}-${showOsmOverlay ? 1 : 0}`}
        onMapLatLngClick={editMode !== 'none' ? handleMapInteraction : undefined}
        onMapLatLngHover={editMode === 'add-turn' ? handleMapHover : undefined}
        mapHoverMarker={editMode === 'add-turn' ? turnHoverCoord ?? undefined : undefined}
        onMapMarkerClick={mapMarkersInteractive ? handleMarkerClick : undefined}
        onMapMarkerDragEnd={turnMarkersInteractive ? handleMarkerDragEnd : undefined}
        startFinishLine={lapDrawComplete || !isMapLayoutMode ? activeGeometry?.startLineSuggestion : undefined}
        replayCursors={isMapLayoutMode ? [] : replayCursors}
        followReplayCursor={false}
        mapMarkerScale={1.85}
        fitBoundsPadding={48}
        maxFitZoom={20}
        minFitZoom={isMapLayoutMode ? undefined : 18}
      />

      {/* ── Left rail / top toolbar ── */}
      {detailsOpen ? (
        <MapOverlay
          interactive
          sx={{
            position: 'absolute',
            top: PANEL_GAP,
            left: PANEL_GAP,
            bottom: bottomDockReserve,
            width: LEFT_PANEL_WIDTH,
            zIndex: 4,
            overflow: 'auto',
            p: PANEL_PAD,
            pointerEvents: 'auto',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1 }}>
            <Stack spacing={0.75} sx={{ minWidth: 0, flex: 1 }}>
              <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
                <Typography variant="subtitle2" fontWeight={800}>
                  Track Enrichment
                </Typography>
                <Chip label="DEV" size="small" color="warning" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800 }} />
              </Stack>
              <SessionPicker
                options={sessionOptions}
                value={selectedSessionOption}
                loading={dataFilesLoading}
                disabled={loading}
                onSelect={(option) => {
                  if (option.key !== sessionKey) {
                    void loadSession(option);
                  }
                }}
              />
              {circuitDisplayName !== sessionMeta?.venue && sessionMeta?.venue ? (
                <Chip
                  icon={<ExploreOutlinedIcon sx={{ fontSize: 14 }} />}
                  label={sessionMeta.venue}
                  size="small"
                  variant="outlined"
                  sx={{ height: 24, alignSelf: 'flex-start', maxWidth: '100%', '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                />
              ) : null}
            </Stack>
            <IconButton size="small" onClick={() => setDetailsOpen(false)} aria-label="Collapse editor panel">
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
          </Stack>

          {layoutWorkflowPanel}
          {editControlsRow}
          <Box sx={{ mt: 1 }}>{statusCaptionLine}</Box>

          <Divider sx={{ my: 1.5 }} />
          {editorPanelBody}
        </MapOverlay>
      ) : (
        <>
          <Box sx={{ position: 'absolute', inset: 0, zIndex: 4, pointerEvents: 'none' }}>
            <MapOverlay
              interactive
              sx={{
                position: 'absolute',
                top: PANEL_GAP,
                left: PANEL_GAP,
                right: rightInset,
                px: PANEL_PAD,
                py: PANEL_PAD,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.25}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ minWidth: 0 }}>
                  <Typography variant="subtitle2" fontWeight={800} noWrap>
                    Track Enrichment
                  </Typography>
                  <Chip label="DEV" size="small" color="warning" sx={{ height: 20, fontSize: '0.62rem', fontWeight: 800 }} />
                  <SessionPicker
                    options={sessionOptions}
                    value={selectedSessionOption}
                    loading={dataFilesLoading}
                    disabled={loading}
                    onSelect={(option) => {
                      if (option.key !== sessionKey) {
                        void loadSession(option);
                      }
                    }}
                  />
                  {circuitDisplayName !== sessionMeta?.venue && sessionMeta?.venue ? (
                    <Chip
                      icon={<ExploreOutlinedIcon sx={{ fontSize: 14 }} />}
                      label={sessionMeta.venue}
                      size="small"
                      variant="outlined"
                      sx={{ height: 24, maxWidth: 140, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }}
                    />
                  ) : null}
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center" flexShrink={0}>
                  {editControlsRow}
                  <IconButton size="small" onClick={() => setDetailsOpen(true)} aria-label="Expand editor panel">
                    <TuneOutlinedIcon fontSize="small" />
                  </IconButton>
                </Stack>
              </Stack>

              <Box sx={{ mt: 1.25 }}>{statusCaptionLine}</Box>
            </MapOverlay>
          </Box>
        </>
      )}

      {reslicedLaps.laps.length > 0 ? (
        <Box
          sx={{
            position: 'absolute',
            left: leftInset,
            right: rightInset,
            bottom: PANEL_GAP,
            zIndex: 5,
            pointerEvents: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 0.75,
            transition: 'left 200ms ease, right 200ms ease',
          }}
        >
          <>
            <Box
              sx={{
                borderRadius: '6px',
                border: '1px solid rgba(36,62,82,0.55)',
                bgcolor: 'rgba(1,13,24,0.45)',
                backdropFilter: 'blur(8px)',
                px: 1,
                py: 0.75,
              }}
            >
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" fontWeight={700} sx={{ fontSize: '0.66rem', letterSpacing: '0.04em' }}>
                  S/F laps
                </Typography>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Button
                    size="small"
                    variant="text"
                    startIcon={<AutorenewIcon sx={{ fontSize: 14 }} />}
                    onClick={handleRegenerateLaps}
                    disabled={!session || !activeGeometry}
                    sx={{ fontSize: '0.62rem', minWidth: 0, px: 0.75, py: 0, height: 22, color: 'text.secondary' }}
                  >
                    Regenerate
                  </Button>
                  <Chip label={formatResliceSummary(resliceKindCounts)} size="small" sx={{ height: 22, fontSize: '0.62rem' }} />
                </Stack>
              </Stack>
              <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ maxHeight: 72, overflow: 'auto' }}>
                {reslicedLaps.laps.map((lap) => {
                  const selected = highlightedReslicedLapNumber === lap.lapNumber;
                  const chipStyles = reslicedLapChipStyles(lap, selected);

                  return (
                    <Chip
                      key={lap.lapNumber}
                      label={formatReslicedLapLabel(lap)}
                      size="small"
                      onClick={() => {
                        preferFlyingLapRef.current = false;
                        setSelectedReslicedLapNumber(lap.lapNumber);
                        setReplayScope('lap');
                      }}
                      sx={{
                        height: 24,
                        fontSize: '0.66rem',
                        fontWeight: 700,
                        cursor: 'pointer',
                        bgcolor: chipStyles.bgcolor,
                        color: chipStyles.color,
                        border: chipStyles.border,
                      }}
                    />
                  );
                })}
              </Stack>
            </Box>
            <TelemetryReplayBar
              replay={replay}
              scope={replayScope}
              onScopeChange={setReplayScope}
              lapNumber={highlightedReslicedLapNumber}
              totalLaps={reslicedLaps.laps.length}
              sessionScopeLabel="All S/F laps"
              sessionLapCountLabel={`${reslicedLaps.laps.length} S/F laps`}
              lapScopeLabel={replayLapScopeLabel}
            />
          </>
        </Box>
      ) : null}

      {/* ── Right review panel ── */}
      {reviewOpen ? (
        <MapOverlay
          sx={{
            position: 'absolute',
            top: PANEL_GAP,
            right: PANEL_GAP,
            bottom: bottomDockReserve,
            width: RIGHT_PANEL_WIDTH,
            zIndex: 3,
            overflow: 'auto',
            pointerEvents: 'auto',
            p: PANEL_PAD,
          }}
        >
          {circuitPanel}
        </MapOverlay>
      ) : null}
      {!reviewOpen ? (
        <IconButton
          size="small"
          onClick={() => setReviewOpen(true)}
          aria-label="Expand circuit panel"
          sx={{
            position: 'absolute',
            top: PANEL_GAP,
            right: PANEL_GAP,
            zIndex: 3,
            bgcolor: OVERLAY_BG,
            border: '1px solid rgba(255,255,255,0.1)',
            '&:hover': { bgcolor: 'rgba(7, 17, 28, 0.95)' },
          }}
        >
          <ChevronLeftIcon fontSize="small" />
        </IconButton>
      ) : null}

    </Box>
  );
}
